import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  BackgroundVariant,
} from '@xyflow/react'
import { RightDragPan } from './RightDragPan'
import { GraphMiniMap } from './GraphMiniMap'
import './nodes/nodeLook.css'
import { ResizeHandle } from './ResizeHandle'
import { shellLayout } from '../lib/shellLayout'
import '@xyflow/react/dist/style.css'
import { useCanvasFocus, useFocusDrill, selectOnlyChanges } from '../lib/useGraphFocus'
import { OnConstructNode } from './nodes/OnConstructNode'
import { CustomEventNode, CallCustomEventNode } from './nodes/CustomEventNode'
import { PrintNode } from './nodes/PrintStringNode'
import { DeleteEdge }      from './nodes/DeleteEdge'
import { eventGraphState, subscribeEventGraphInsert } from '../lib/eventGraphState'
import { customEventNames } from '../lib/customEvents'
import { dropDisplacedEdges } from '../lib/graphConnectPolicy'
import { useGraphClipboard } from '../lib/useGraphClipboard'
import { createMinter } from '../lib/nodeClipboard'
import { resolveCharacterId } from '../lib/characterState'
import { availableRefsFor } from '../lib/graphAvailableRefs'
import GraphClipboardStatus from './GraphClipboardStatus'
import { GRAPH_MULTISELECT_PROPS } from '../lib/graphInteractionProps'
import { alignNodes, distributeNodes, straightenConnections, tidyGraph, arrangeShortcutFor } from '../lib/graphArrange'
import NodeContextMenu from './nodes/NodeContextMenu'
import CommentNode from './nodes/CommentNode.jsx'
import { COMMENT_ACCENT } from './nodes/commentTheme.js'
import {
  COMMENT_TYPE, createComment, withCommentBehind, markCommentForEdit,
  boundsAroundNodes, DEFAULT_COMMENT_SIZE,
} from '../lib/graphComment.js'
import { useStableNodeData } from '../lib/useStableNodeData'
import { GraphToolbar } from './GraphToolbar'
import { EVENT_NODE_TYPES as EVENT_TYPES } from '../lib/eventNodeTypes'
import { listNodesByType, findGraphNodes } from '../lib/graphNodeSearch'
import { requestFocus } from '../lib/graphFocus'
import BlueprintOutlineSection from './blueprint/BlueprintOutlineSection'
import PaletteGraphMatches from './blueprint/PaletteGraphMatches'
import { readGraphs } from '../lib/liveGraphs'
import { useRememberedViewport } from '../lib/useGraphViewport'
import { createGraphHistory, isUndoKeyEvent } from '../lib/graphHistory'
import { dialog } from '../lib/dialog'
import {
  findDeclarationRefs, removeDeclarationRefs, describeRefs,
  duplicateVariableDef, duplicateFunctionDef,
} from '../lib/declarationRefs'
import { devLog } from '../lib/devLog'
import { CAMERA_RIG_TEMPLATES, instantiateRigAsGroup } from '../lib/cameraRigTemplates'
import { cameraRigState } from '../lib/cameraRigCompat'
import { GraphGroupNode } from './nodes/GraphGroupNode'
import { MacroNode, MacroInputNode, MacroOutputNode, MACRO_ACCENT, MACRO_PIN_COLOR } from './nodes/MacroNodes'
import {
  defaultMacroData, collapseToMacro, isForbiddenInMacro, isMacroTunnelType, prunePinEdges,
  nextPinId, uniquePinName, MACRO_PIN_TYPES, MACRO_IN_ID, MACRO_OUT_ID, reidBody,
} from '../lib/macroGraph'
import {
  WSConnectedNode,
  WSDisconnectedNode,
  NewResponseNode,
  PrefabTriggerNode,
  ResponseCompleteNode,
  OnGestureNode,
  OnSentenceStartedNode,
  OnSentenceDataReadyNode,
  OnFirstSentenceReadyNode,
  OnSentenceCompletedNode,
  OnLastSentenceStartedNode,
  OnAllSentencesPlayedNode,
  OnTextInputNode,
  OnUserTypingNode,
  OnUserTypingCancelledNode,
  OnSessionNode,
  OnMetricsNode,
  OnSpeechStartNode,
  OnSpeechEndNode,
  OnBargeInNode,
  OnSentenceWaitingNode,
  OnServerErrorNode,
  ConvorcherSessionNode,
} from './nodes/ConvorcherEventNodes'
import { PlayFirstSentenceNode, PlayNextSentenceNode, SendTextInputNode, SayLineNode, StopSpeechNode, PlayFaceClipNode, ListClipsNode, LogStateNode, PlayGestureNode, RestorePreviousStateNode, PlayBodyClipNode, PlayAudioNode, PlayPrefabNode, StreamFirstFrameNode, StreamLastFrameNode } from './nodes/SentenceControlNodes'
import { ParlaySTTNode } from './nodes/ParlaySTTNode'
import LoadEnvironmentNode from './nodes/LoadEnvironmentNode'
import LoadGarmentNode from './nodes/LoadGarmentNode'
import { VoxyConfigNode } from './nodes/VoxyConfigNode'
import { constants } from '../lib/animVariables'
import { DelayNode, InputKeyNode, BranchNode, SequenceNode, SEQUENCE_ACCENT, MakeLiteralNode } from './nodes/UtilityNodes'
import { SEQUENCE_MIN_OUTPUTS } from '../lib/sequenceNode.js'
import { functionColor } from './nodes/functionColor.js'
import { SetVariableNode } from './nodes/SetVariableNode'
import { RandomNode } from './nodes/RandomNode'
import { SelectNode } from './nodes/SelectNode'
import { ConcatStringNode } from './nodes/ConcatStringNode'
import { ComparisonNode } from './nodes/ComparisonNode'
import { LogicalNode } from './nodes/LogicalNode'
import { MathNode } from './nodes/MathNode'
import { COMPARISON_ACCENT, LOGICAL_ACCENT, MATH_ACCENT } from './nodes/operatorTheme'
import { RendezvousNode, RendezvousIndexedNode, MergeNode, MERGE_MIN_INPUTS, RENDEZVOUS_ACCENT } from './nodes/RendezvousNode'
import { SmoothDampNode, SMOOTH_DAMP_ACCENT } from './nodes/SmoothDampNode'
import { VarGetNode } from './nodes/rule/VarGetNode'
import {
  ArrayGetNode, ArraySetNode, ArrayAddNode, ArrayRemoveNode, ArrayLengthNode, ArrayClearNode,
  MapGetNode, MapSetNode, MapRemoveNode, MapHasKeyNode, MapLengthNode, MapClearNode,
} from './nodes/ArrayMapNodes'
import { FunctionCallNode, FunctionEntryNode, FunctionReturnNode } from './nodes/FunctionNodes'
import { MoveToLocationNode, MoveToActorNode, MoveToRandomPointNode, AbortMovementNode, OnMoveCompletedNode } from './nodes/WayfindingNodes'
import {
  GetCharacterLocationNode, GetCharacterRotationNode, GetCharacterTransformNode,
  GetCharacterForwardNode, GetCharacterRightNode, GetCharacterUpNode,
  SetCharacterLocationNode, SetCharacterRotationNode, SetCharacterTransformNode,
} from './nodes/CharacterTransformNodes'
import {
  SetCameraFovNode, SetCameraTargetNode,
  SetCameraCageNode, SetCameraSeeThroughNode, SetCameraFollowNode, GetCameraTransformNode,
  CameraAddYawNode, CameraAddPitchNode, CameraAddDollyNode, CameraPanTargetNode,
  CameraSetRigLimitsNode, CameraSetRigPoseNode, HeadTrackedCameraNode,
} from './nodes/CameraNodes'
import SetBackdropNode from './nodes/SetBackdropNode'
// The scene-colour defaults, imported rather than repeated: sceneColorDefaults
// is the one functional home the palette lock allowlists, and a second copy of
// these hexes is a second thing to update.
import { BACKDROP_SKY, BACKDROP_GROUND } from '../lib/sceneColorDefaults'
import { HEAD_TRACK_DEFAULTS } from '../lib/headTrackedCamera'
import { CAMERA_ACCENT } from '../lib/cameraTheme'
import { EVENTS_ACCENT } from './nodes/eventsTheme'
import { ACTIONS_ACCENT } from './nodes/actionsTheme'
import {
  OnPointerDownNode, OnPointerUpNode, OnPointerDragNode, OnWheelNode,
  OnKeyDownNode, OnKeyUpNode, IsButtonHeldNode, IsKeyHeldNode, GetInputAxisNode,
} from './nodes/InputNodes'
import AnchoredOverlay from './AnchoredOverlay'

// ─── Node type registry ───────────────────────────────────────────────────────
const EVENT_NODE_TYPES = {
  [COMMENT_TYPE]: CommentNode,
  // Wave 2B: beginPlay merged into onConstruct. The alias keeps an
  // UNMIGRATED in-memory graph (e.g. a test injecting the old type straight
  // into eventGraphState) rendering instead of showing a broken node; every
  // hydration boundary retypes it for real (blueprintMigrations.js).
  beginPlay:              OnConstructNode,
  onConstruct:            OnConstructNode,
  customEvent:            CustomEventNode,
  callCustomEvent:        CallCustomEventNode,
  print:                  PrintNode,
  printString:            PrintNode,
  playFirstSentence:         PlayFirstSentenceNode,
  playNextSentence:          PlayNextSentenceNode,
  sendTextInput:             SendTextInputNode,
  sendTextOut:               SayLineNode,
  sendVoiceTextMessage:      SendTextInputNode,
  delay:                     DelayNode,
  branch:                    BranchNode,
  sequence:                  SequenceNode,
  setVariable:               SetVariableNode,
  inputKey:                  InputKeyNode,
  onWSConnected:          WSConnectedNode,
  onWSDisconnected:       WSDisconnectedNode,
  onNewResponse:          NewResponseNode,
  onPrefabTrigger:        PrefabTriggerNode,
  onResponseComplete:     ResponseCompleteNode,
  onGesture:              OnGestureNode,
  onAnimation:            OnGestureNode, // backward-compat alias
  onSentenceStarted:      OnSentenceStartedNode,
  onSentenceDataReady:    OnSentenceDataReadyNode,
  onFirstSentenceReady:   OnFirstSentenceReadyNode,
  onSentenceCompleted:    OnSentenceCompletedNode,
  onLastSentenceStarted:  OnLastSentenceStartedNode,
  onAllSentencesPlayed:   OnAllSentencesPlayedNode,
  onTextInput:            OnTextInputNode,
  onUserTyping:           OnUserTypingNode,
  onUserTypingCancelled:  OnUserTypingCancelledNode,
  onSession:              OnSessionNode,
  onMetrics:              OnMetricsNode,
  onSpeechStart:          OnSpeechStartNode,
  onSpeechEnd:            OnSpeechEndNode,
  onBargeIn:              OnBargeInNode,
  onSentenceWaiting:      OnSentenceWaitingNode,
  onServerError:          OnServerErrorNode,
  stopSpeech:             StopSpeechNode,
  streamFirstFrame:       StreamFirstFrameNode,
  streamLastFrame:        StreamLastFrameNode,
  playFaceClip:           PlayFaceClipNode,
  listClips:              ListClipsNode,
  logState:               LogStateNode,
  playGesture:            PlayGestureNode,
  restorePreviousState:   RestorePreviousStateNode,
  playBodyClip:           PlayBodyClipNode,
  playAudio:              PlayAudioNode,
  playPrefab:             PlayPrefabNode,
  loadEnvironment:        LoadEnvironmentNode,
  loadGarment:            LoadGarmentNode,
  setBackdrop:            SetBackdropNode,
  parlaySTT:              ParlaySTTNode,
  voxyConfig:             VoxyConfigNode,
  convorcherSession:      ConvorcherSessionNode,
  random:                 RandomNode,
  select:                 SelectNode,
  concatString:           ConcatStringNode,
  // Comparison operators (one component, keyed by type)
  equal:                  ComparisonNode,
  notEqual:               ComparisonNode,
  greater:                ComparisonNode,
  less:                   ComparisonNode,
  greaterEqual:           ComparisonNode,
  lessEqual:              ComparisonNode,
  // Logical operators (one component, keyed by type)
  and:                    LogicalNode,
  or:                     LogicalNode,
  not:                    LogicalNode,
  // Math operators (one component, keyed by type)
  add:                    MathNode,
  subtract:               MathNode,
  multiply:               MathNode,
  divide:                 MathNode,
  rendezvous:             RendezvousNode,
  merge:                  MergeNode,
  rendezvousIndexed:      RendezvousIndexedNode,
  smoothDamp:             SmoothDampNode,
  makeLiteralString:      MakeLiteralNode,
  makeLiteralFloat:       MakeLiteralNode,
  makeLiteralInt:         MakeLiteralNode,
  getVariable:            VarGetNode,
  // TArray
  arrayGet:              ArrayGetNode,
  arraySet:              ArraySetNode,
  arrayAdd:              ArrayAddNode,
  arrayRemove:           ArrayRemoveNode,
  arrayLength:           ArrayLengthNode,
  arrayClear:            ArrayClearNode,
  // TMap
  mapGet:                MapGetNode,
  mapSet:                MapSetNode,
  mapRemove:             MapRemoveNode,
  mapHasKey:             MapHasKeyNode,
  mapLength:             MapLengthNode,
  mapClear:              MapClearNode,
  // Functions
  functionCall:          FunctionCallNode,
  functionEntry:         FunctionEntryNode,
  functionReturn:        FunctionReturnNode,
  // Wayfinding / movement (ENG-592)
  moveToLocation:        MoveToLocationNode,
  moveToActor:           MoveToActorNode,
  moveToRandomPoint:     MoveToRandomPointNode,
  abortMovement:         AbortMovementNode,
  onMoveCompleted:       OnMoveCompletedNode,
  // Character → Transform (Unreal-style getters/setters)
  getCharacterLocation:  GetCharacterLocationNode,
  getCharacterRotation:  GetCharacterRotationNode,
  getCharacterTransform: GetCharacterTransformNode,
  getCharacterForward:   GetCharacterForwardNode,
  getCharacterRight:     GetCharacterRightNode,
  getCharacterUp:        GetCharacterUpNode,
  setCharacterLocation:  SetCharacterLocationNode,
  setCharacterRotation:  SetCharacterRotationNode,
  setCharacterTransform: SetCharacterTransformNode,
  // Camera (camera-in-blueprint)
  setCameraFov:          SetCameraFovNode,
  setCameraTarget:       SetCameraTargetNode,
  setCameraCage:         SetCameraCageNode,
  setCameraSeeThrough:   SetCameraSeeThroughNode,
  setCameraFollow:       SetCameraFollowNode,
  getCameraTransform:    GetCameraTransformNode,
  // Camera rig motion — the camera IS these nodes now; there is no preset underneath.
  cameraAddYaw:          CameraAddYawNode,
  cameraAddPitch:        CameraAddPitchNode,
  cameraAddDolly:        CameraAddDollyNode,
  cameraPanTarget:       CameraPanTargetNode,
  cameraSetRigLimits:    CameraSetRigLimitsNode,
  cameraSetRigPose:      CameraSetRigPoseNode,
  headTrackedCamera:     HeadTrackedCameraNode,
  // User input
  onPointerDown:         OnPointerDownNode,
  onPointerUp:           OnPointerUpNode,
  onPointerDrag:         OnPointerDragNode,
  onWheel:               OnWheelNode,
  onKeyDown:             OnKeyDownNode,
  onKeyUp:               OnKeyUpNode,
  isButtonHeld:          IsButtonHeldNode,
  isKeyHeld:             IsKeyHeldNode,
  getInputAxis:          GetInputAxisNode,
  // Graph groups — one level of nesting (camera-in-blueprint Phase 3)
  graphGroup:            GraphGroupNode,
  // Macros — collapsed sub-graphs with exec/data tunnel pins (macroGraph.js)
  macro:                 MacroNode,
  macroInput:            MacroInputNode,
  macroOutput:           MacroOutputNode,
}
const EDGE_TYPES = { default: DeleteEdge }

// Changes to one variable's value closer together than this are one undo step.
const VALUE_EDIT_MERGE_MS = 800

// Node types that count as "events" (shown in MY BLUEPRINT left panel).
// EVENT_TYPES is imported from ../lib/eventNodeTypes — the single source
// also used by execReachability.js (ENG-981 headTrackedCamera disarm fix),
// so the two never drift apart.

// Context-menu catalog
const EVENT_PALETTE = [
  { type: COMMENT_TYPE, label: 'Comment', desc: 'A note on the graph. Drag it to move everything inside it.', color: COMMENT_ACCENT, category: 'Comment' },
  // ── Events ──
  // beginPlay is gone from the palette (Wave 2B): onConstruct is the one
  // entry event — it fires once the avatar loads, and again on every edit.
  { type: 'onConstruct',           label: 'Event On Construct',       desc: 'Fires once the avatar loads, and again after every editor edit', color: EVENTS_ACCENT, category: 'Events',  unique: true  },
  { type: 'onWSConnected',         label: 'On WS Connected',          desc: 'WebSocket connection established',                      color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onNewResponse',         label: 'On New Response',          desc: 'Standard streamed response (no prefab trigger)',        color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onPrefabTrigger',       label: 'On Prefab Trigger',        desc: 'Fires when a prefab/animation trigger arrives',         color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onResponseComplete',    label: 'On Response Complete',     desc: 'All sentence data received from server',                color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onGesture',             label: 'On Gesture',               desc: 'Sentence carries a gesture tag from the server',        color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSentenceStarted',     label: 'On Sentence Started',      desc: 'A sentence begins playing',                             color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSentenceDataReady',   label: 'On Sentence Data Ready',   desc: 'A sentence\'s data is ready (frames in) — per sentence, may be out of order', color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onFirstSentenceReady',  label: 'On First Sentence Ready',   desc: 'The opening sentence (0) is ready to play — fires once per response', color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSentenceCompleted',   label: 'On Sentence Completed',    desc: 'A sentence finishes playing',                           color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onLastSentenceStarted', label: 'On Last Sentence Started', desc: 'Final sentence begins playing',                         color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onAllSentencesPlayed',  label: 'On All Sentences Played',  desc: 'Last sentence has finished playing',                    color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onTextInput',           label: 'On Text Input',            desc: 'User submitted text from the input bar',                color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onUserTyping',          label: 'On User Typing',           desc: 'User started typing in the input bar',                  color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onUserTypingCancelled', label: 'On User Typing Cancelled', desc: 'User cleared the input after typing',                   color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSession',             label: 'On Session',               desc: 'Session established with server',                       color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onMetrics',             label: 'On Metrics',               desc: 'Performance metrics from server',                       color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSpeechStart',        label: 'On Speech Start',          desc: 'User started speaking (VAD triggered)',                 color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSpeechEnd',          label: 'On Speech End',            desc: 'User stopped speaking (silence timeout)',               color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onBargeIn',            label: 'On Barge In',             desc: 'User spoke while avatar was talking',                   color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onSentenceWaiting',   label: 'On Sentence Waiting',    desc: 'Next sentence queued but frames not ready (delay gap)', color: '#ff7040', category: 'Events',  unique: true  },
  { type: 'onServerError',        label: 'On Server Error',         desc: 'Server sent an Error message',                         color: '#ff4040', category: 'Events',  unique: true  },
  { type: 'onMoveCompleted',      label: 'On Move Completed',        desc: 'Fires when a navmesh move ends (success/failed/aborted)', color: '#ff7040', category: 'Events',  unique: false },
  { type: 'customEvent',          label: 'Custom Event',             desc: 'A named event you can fire from a state-machine transition’s Actions or a Call Custom Event node', color: EVENTS_ACCENT, category: 'Events',  unique: false },
  { type: 'callCustomEvent',      label: 'Call Custom Event',        desc: 'Fire a Custom Event by name, run its chain, then continue', color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'inputKey',              label: 'Input Key',                desc: 'Fires when a bound key is pressed',                     color: '#ff7040', category: 'Events',  unique: false },
  { type: 'onPointerDown',         label: 'On Pointer Down',          desc: 'Viewport mouse/touch button pressed. Filter by button.',                  color: '#ff7040', category: 'Input', unique: false },
  { type: 'onPointerUp',           label: 'On Pointer Up',            desc: 'Viewport button released (or canceled by the browser).',                  color: '#ff7040', category: 'Input', unique: false },
  { type: 'onPointerDrag',         label: 'On Pointer Drag',          desc: 'Fires once per frame while a button is held and the pointer moved — dx/dy coalesced for that frame', color: '#ff7040', category: 'Input', unique: false },
  { type: 'onWheel',               label: 'On Wheel',                 desc: 'Fires once per frame with the wheel delta, normalized to pixels',         color: '#ff7040', category: 'Input', unique: false },
  { type: 'onKeyDown',             label: 'On Key Down',              desc: 'Bound key pressed while the viewport is hovered/focused (no auto-repeat)', color: '#ff7040', category: 'Input', unique: false },
  { type: 'onKeyUp',               label: 'On Key Up',                desc: 'Bound key released — always delivered, so a latch cannot stick',           color: '#ff7040', category: 'Input', unique: false },
  { type: 'isButtonHeld',          label: 'Is Button Held',           desc: 'True while a mouse button is down (data read — this is how you do "hold")', color: '#7ad29a', category: 'Input', unique: false },
  { type: 'isKeyHeld',             label: 'Is Key Held',              desc: 'True while the bound key is down',                                        color: '#7ad29a', category: 'Input', unique: false },
  { type: 'getInputAxis',          label: 'Get Input Axis',           desc: 'Live axis: mouseX/mouseY (this frame), wheel, pointerX/pointerY',         color: '#7ad29a', category: 'Input', unique: false },
  { type: 'cameraAddYaw',          label: 'Camera Add Yaw',           desc: 'Orbit the camera horizontally by delta × sensitivity radians',            color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'cameraAddPitch',        label: 'Camera Add Pitch',         desc: 'Orbit the camera vertically (clamped by the rig pitch limits)',           color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'cameraAddDolly',        label: 'Camera Add Dolly',         desc: 'Move toward/away from the target — multiplicative, so it feels the same at any distance', color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'cameraPanTarget',       label: 'Camera Pan Target',        desc: 'Slide the look-at along screen right/up, scaled by distance',             color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'cameraSetRigLimits',    label: 'Camera Set Rig Limits',    desc: 'Bound dolly distance + pitch — what a preset used to hardcode',           color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'cameraSetRigPose',      label: 'Camera Set Rig Pose',      desc: 'Place the camera at an absolute yaw/pitch/dist/target, optionally eased', color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'headTrackedCamera',     label: 'Head-Tracked Camera',      desc: 'Webcam head tracking adds a parallax offset on top of the camera (MediaPipe, in-browser). Off by default.', color: CAMERA_ACCENT, category: 'Camera', unique: true },
  // "Presets", as graphs. These insert a cluster of ordinary, editable nodes — the whole
  // replacement for the deleted setCameraPreset enum. `rig` keys into CAMERA_RIG_TEMPLATES.
  { type: 'rigOrbit',              label: '＋ Orbit Camera Rig',       desc: 'Insert the orbit preset as a graph group: left-drag orbits, right-drag pans, middle-drag / wheel dollies. Double-click to open and edit.', color: CAMERA_ACCENT, category: 'Camera Rigs', unique: false, rig: 'orbit' },
  { type: 'rigThirdPerson',        label: '＋ Third-Person Camera Rig', desc: 'Insert the over-the-shoulder preset as a graph group: tight distance band, no pan, stays above the floor.', color: CAMERA_ACCENT, category: 'Camera Rigs', unique: false, rig: 'thirdPerson' },
  // ── Groups (camera-in-blueprint Phase 3) ──
  { type: 'macro',                 label: '＋ Macro',                  desc: 'Collapsed sub-graph with exec + data in/out pins. Delays and other latent nodes inside work. Double-click to open; select nodes and right-click ▸ Collapse to Macro to make one from existing logic.', color: MACRO_ACCENT, category: 'Groups', unique: false },
  { type: 'graphGroup',            label: '＋ Graph Group',            desc: 'Sub-graph container — double-click to open, then add nodes inside (events included). One level of nesting only.', color: '#4fc0c8', category: 'Groups', unique: false },
  // ── Actions ──
  { type: 'playFirstSentence',     label: 'Play First Sentence',      desc: 'Start playing sentence 0 from the queue',               color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'playNextSentence',      label: 'Play Next Sentence',       desc: 'Advance to the next queued sentence',                   color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'sendTextInput',         label: 'Send Text Input',          desc: 'Send text to the AI and trigger a response',            color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'sendTextOut',           label: 'Say Line',                 desc: 'Make the character say this exact line (no LLM); [brackets] play as gestures', color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'stopSpeech',            label: 'Stop Speech',              desc: 'Stop audio and clear sentence queue',                   color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'streamFirstFrame',      label: 'Stream First Frame',       desc: 'Publish a sentence\'s first frame as a static face pose (First Frame Pose node)', color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'streamLastFrame',       label: 'Stream Last Frame',        desc: 'Publish a sentence\'s last frame as a static face pose (Last Frame Pose node)',   color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'playBodyClip',          label: 'Play Body Clip',           desc: 'Play a body clip once and wait for it to finish',        color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'playAudio',             label: 'Play Audio',               desc: 'Play a content-browser audio clip one-shot (pick from the dropdown, or a custom URL)',         color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'playPrefab',            label: 'Play Prefab',              desc: 'Play a custom prefab: body + face + audio in sync',      color: '#c080ff', category: 'Actions', unique: false },
  { type: 'loadEnvironment',       label: 'Load Environment',         desc: 'Load one of the org gsplat environments — wire from On Construct for the blueprint default, or from a key press', color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'loadGarment',           label: 'Load Garment',             desc: "Dress the character in one of its garment assets — wire from On Construct for the outfit it boots in, or from a key press to change clothes", color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'setBackdrop',           label: 'Set Backdrop',             desc: 'Set the viewport sky color and ground plane. Part of the rendered frame, so it shows in recordings. Settings ▸ Scene writes one of these when you save a look to the character.', color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'moveToLocation',        label: 'Move To Location',         desc: 'Walk the avatar to a world XYZ point on the navmesh',    color: '#5aa0ff', category: 'Movement', unique: false },
  { type: 'moveToActor',           label: 'Move To Actor',            desc: 'Walk to a named scene object / marker',                 color: '#5aa0ff', category: 'Movement', unique: false },
  { type: 'moveToRandomPoint',     label: 'Move To Random Point',     desc: 'Walk to a random reachable point within a radius',      color: '#5aa0ff', category: 'Movement', unique: false },
  { type: 'abortMovement',         label: 'Abort Movement',           desc: 'Stop any active move (UE StopMovement)',                color: '#5aa0ff', category: 'Movement', unique: false },
  { type: 'getCharacterLocation',  label: 'Get Character Location',   desc: 'Avatar world position as a vector [x,y,z] (UE GetActorLocation)',        color: '#7ad29a', category: 'Character', unique: false },
  { type: 'getCharacterRotation',  label: 'Get Character Rotation',   desc: 'Avatar rotation as Euler degrees [x,y,z] (UE GetActorRotation)',         color: '#7ad29a', category: 'Character', unique: false },
  { type: 'getCharacterTransform', label: 'Get Character Transform',  desc: 'Avatar transform {location,rotation,scale} (UE GetActorTransform)',      color: '#7ad29a', category: 'Character', unique: false },
  { type: 'getCharacterForward',   label: 'Get Character Forward',    desc: 'Avatar forward unit vector (UE GetActorForwardVector)',                  color: '#7ad29a', category: 'Character', unique: false },
  { type: 'getCharacterRight',     label: 'Get Character Right',      desc: 'Avatar right unit vector (UE GetActorRightVector)',                      color: '#7ad29a', category: 'Character', unique: false },
  { type: 'getCharacterUp',        label: 'Get Character Up',         desc: 'Avatar up unit vector (UE GetActorUpVector)',                            color: '#7ad29a', category: 'Character', unique: false },
  { type: 'setCharacterLocation',  label: 'Set Character Location',   desc: 'Teleport the avatar to a world position (UE SetActorLocation)',          color: '#e0a060', category: 'Character', unique: false },
  { type: 'setCharacterRotation',  label: 'Set Character Rotation',   desc: 'Set the avatar rotation in Euler degrees (UE SetActorRotation)',         color: '#e0a060', category: 'Character', unique: false },
  { type: 'setCharacterTransform', label: 'Set Character Transform',  desc: 'Apply a full transform to the avatar (UE SetActorTransform)',            color: '#e0a060', category: 'Character', unique: false },
  { type: 'setCameraFov',          label: 'Set Camera FOV',           desc: 'Set the camera field of view in degrees',                               color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'setCameraTarget',       label: 'Set Camera Target',        desc: 'Point the camera at a vector or the avatar head',                        color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'setCameraCage',         label: 'Set Camera Cage',          desc: 'Confine the camera to an AABB around the avatar (no dolly through walls)', color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'setCameraSeeThrough',   label: 'Set Camera See-Through',   desc: 'Fade environment splats between the camera and the avatar head (manual or auto)', color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'setCameraFollow',       label: 'Set Camera Follow',        desc: 'Continuously frame the avatar as it moves (offset + damping)',           color: CAMERA_ACCENT, category: 'Camera', unique: false },
  { type: 'getCameraTransform',    label: 'Get Camera Transform',     desc: 'Live camera { location, rotation, target, fov }',                        color: '#7ad29a', category: 'Camera', unique: false },
  { type: 'parlaySTT',             label: 'Parlay STT',               desc: 'Transcribe the last utterance via Parlay — wire out-transcript into SendTextInput', color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'voxyConfig',            label: 'Voxy Config',              desc: 'Initialize mic + VAD on first fire, or update thresholds live. Wire from beginPlay to start the mic.', color: ACTIONS_ACCENT, category: 'Actions', unique: true },
  { type: 'convorcherSession',     label: 'Convorcher Session',       desc: 'Declare the convorcher session (character + features) from the graph and expose session_id. Wire from beginPlay. Lets a blueprint converse as a persona distinct from the rendered character.', color: '#ff7040', category: 'Actions', unique: true },
  { type: 'setVariable',           label: 'Set Variable',             desc: 'Set an animation variable at runtime',                  color: '#40b8d0', category: 'Actions', unique: false },
  { type: 'branch',                label: 'Branch',                   desc: 'Fork exec on a bool condition (True / False)',           color: '#a0a0d0', category: 'Actions', unique: false },
  { type: 'sequence',              label: 'Sequence',                 desc: 'Fire Then 0, Then 1, … in order, every time. Add pins for more outputs', color: SEQUENCE_ACCENT, category: 'Actions', unique: false },
  { type: 'merge',                 label: 'Merge (OR-join)',         desc: 'Whichever input arrives continues out — rejoin a Branch’s True and False into one chain', color: RENDEZVOUS_ACCENT, category: 'Actions', unique: false },
  { type: 'rendezvous',            label: 'Rendezvous (AND-join)',    desc: 'Fires only once BOTH inputs A and B have arrived (auto-resets)', color: RENDEZVOUS_ACCENT, category: 'Actions', unique: false },
  { type: 'rendezvousIndexed',     label: 'Indexed Rendezvous',       desc: 'AND-join over an index: fires when A and B arrive with the same index (either order)', color: RENDEZVOUS_ACCENT, category: 'Actions', unique: false },
  { type: 'smoothDamp',            label: 'Smooth Damp',              desc: 'Ease a number from Current toward Target with a critically-damped spring — no overshoot, frame-rate independent. Async: Then fires at once, Progress every frame with the Value, Finished on settle.', color: SMOOTH_DAMP_ACCENT, category: 'Actions', unique: false },
  { type: 'delay',                 label: 'Delay',                    desc: 'Wait N seconds then continue exec',                     color: ACTIONS_ACCENT, category: 'Actions', unique: false },
  { type: 'print',                 label: 'Print',                    desc: 'Print to viewport and console (auto-formats objects)',   color: '#6090d0', category: 'Actions', unique: false },
  // ── Math ──
  { type: 'random',                label: 'Random',                   desc: 'Generate a random float or int value',                  color: '#c080e0', category: 'Math',    unique: false },
  { type: 'select',                label: 'Select',                   desc: 'Select one of N values by index',                       color: '#e09030', category: 'Math',    unique: false },
  // Comparison Operators — two value inputs → bool
  { type: 'equal',                 label: 'Equal (==)',               desc: 'A == B (type-aware: bool/int/float/string) → bool',     color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  { type: 'notEqual',              label: 'Not Equal (!=)',           desc: 'A != B → bool',                                         color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  { type: 'greater',               label: 'Greater (>)',              desc: 'A > B (numeric when both numeric, else lexicographic)', color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  { type: 'less',                  label: 'Less (<)',                 desc: 'A < B → bool',                                          color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  { type: 'greaterEqual',          label: 'Greater or Equal (>=)',    desc: 'A >= B → bool',                                         color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  { type: 'lessEqual',             label: 'Less or Equal (<=)',       desc: 'A <= B → bool',                                         color: COMPARISON_ACCENT, category: 'Comparison Operators', unique: false },
  // Logical Operators — bool in, bool out
  { type: 'and',                   label: 'And (&&)',                 desc: 'A && B → bool (inputs coerced to bool)',                color: LOGICAL_ACCENT,    category: 'Logical Operators',    unique: false },
  { type: 'or',                    label: 'Or (||)',                  desc: 'A || B → bool',                                         color: LOGICAL_ACCENT,    category: 'Logical Operators',    unique: false },
  { type: 'not',                   label: 'Not (!)',                  desc: '!A → bool (single input)',                              color: LOGICAL_ACCENT,    category: 'Logical Operators',    unique: false },
  // Math Operators — two value inputs → number (int/float toggle per node)
  { type: 'add',                   label: 'Add (+)',                  desc: 'A + B → number (int/float toggle; operands coerce, non-numeric → 0)', color: MATH_ACCENT, category: 'Math Operators', unique: false },
  { type: 'subtract',              label: 'Subtract (−)',             desc: 'A − B → number',                                        color: MATH_ACCENT,       category: 'Math Operators',       unique: false },
  { type: 'multiply',              label: 'Multiply (×)',             desc: 'A × B → number',                                        color: MATH_ACCENT,       category: 'Math Operators',       unique: false },
  { type: 'divide',                label: 'Divide (÷)',               desc: 'A ÷ B → number (÷0 → 0)',                               color: MATH_ACCENT,       category: 'Math Operators',       unique: false },
  { type: 'concatString',          label: 'Concat String',            desc: 'Join N strings end-to-end (e.g. build a URL)',           color: '#c060e8', category: 'Math',    unique: false },
  { type: 'makeLiteralString',     label: 'Make Literal String',      desc: 'Constant string value to wire into data pins',          color: '#c060e8', category: 'Math',    unique: false },
  { type: 'makeLiteralFloat',      label: 'Make Literal Float',       desc: 'Constant float value to wire into data pins',           color: '#60a0d0', category: 'Math',    unique: false },
  { type: 'makeLiteralInt',        label: 'Make Literal Int',         desc: 'Constant int value to wire into data pins',             color: '#60d090', category: 'Math',    unique: false },
  { type: 'getVariable',           label: 'Get Variable',             desc: 'Read an animation variable value as a data pin',        color: '#d09090', category: 'Math',    unique: false },
  // ── Collections — TArray ──
  { type: 'arrayGet',    label: 'Array Get',    desc: 'Get element at index from an array variable',             color: '#30c0a0', category: 'Collections', unique: false },
  { type: 'arraySet',    label: 'Array Set',    desc: 'Set element at index in an array variable',               color: '#30c0a0', category: 'Collections', unique: false },
  { type: 'arrayAdd',    label: 'Array Add',    desc: 'Append element to the end of an array variable',          color: '#30c0a0', category: 'Collections', unique: false },
  { type: 'arrayRemove', label: 'Array Remove', desc: 'Remove element at index from an array variable',          color: '#30c0a0', category: 'Collections', unique: false },
  { type: 'arrayLength', label: 'Array Length', desc: 'Output the number of elements in an array variable',      color: '#30c0a0', category: 'Collections', unique: false },
  { type: 'arrayClear',  label: 'Array Clear',  desc: 'Remove all elements from an array variable',              color: '#30c0a0', category: 'Collections', unique: false },
  // ── Collections — TMap ──
  { type: 'mapGet',      label: 'Map Get',      desc: 'Get value by key (int or string) from a map variable',    color: '#d09020', category: 'Collections', unique: false },
  { type: 'mapSet',      label: 'Map Set',      desc: 'Set a key→value pair in a map variable',                  color: '#d09020', category: 'Collections', unique: false },
  { type: 'mapRemove',   label: 'Map Remove',   desc: 'Remove a key from a map variable',                        color: '#d09020', category: 'Collections', unique: false },
  { type: 'mapHasKey',   label: 'Map Has Key',  desc: 'Output bool — true if key exists in the map variable',    color: '#d09020', category: 'Collections', unique: false },
  { type: 'mapLength',   label: 'Map Length',   desc: 'Output the number of keys in a map variable',             color: '#d09020', category: 'Collections', unique: false },
  { type: 'mapClear',    label: 'Map Clear',    desc: 'Remove all keys from a map variable',                     color: '#d09020', category: 'Collections', unique: false },
  // ── Debug ──
  { type: 'playFaceClip',          label: 'Play Face Clip',           desc: 'Manually trigger a face clip for 5s',                   color: ACTIONS_ACCENT, category: 'Debug',   unique: false },
  { type: 'listClips',             label: 'List Clips',               desc: 'Log body + face clips to console',                      color: ACTIONS_ACCENT, category: 'Debug',   unique: false },
  { type: 'logState',              label: 'Log State',                desc: 'Dump state machines + variables',                       color: ACTIONS_ACCENT, category: 'Debug',   unique: false },
  { type: 'playGesture',           label: 'Play Gesture',             desc: 'Trigger a face gesture by name (wired pin)',             color: ACTIONS_ACCENT, category: 'Debug',   unique: false },
  { type: 'restorePreviousState',  label: 'Restore Previous State',   desc: 'ConversationalState = PreviousConversationalState',      color: ACTIONS_ACCENT, category: 'Debug',   unique: false },
  // Visible only inside function editor (filtered by isFunctionEditor flag)
  { type: 'functionReturn', label: 'Return', desc: 'End the function and return output values to the caller', color: '#9060d0', category: 'Functions', unique: false, fnOnly: true },
]

// Node types whose singleton-ness must hold across the WHOLE blueprint, not just one graph pool
// (camera-in-blueprint Phase 3 groups). voxyConfig double-inits the mic/VAD and convorcherSession
// double-declares the session if minted once at top level and once inside a group (or in two
// different groups); headTrackedCamera is the same shape — one webcam/MediaPipe pipeline, so a
// duplicate would double-configure (and double-sync) it (ENG-981). This is deliberately NOT true of Events-category unique types (e.g.
// beginPlay): a group needs its OWN beginPlay to be reachable at all (EventRuntime dispatches
// beginPlay against every group's pool independently — see graphGroup.js's collectGroupStarters),
// so duplicate beginPlay nodes across sibling groups are intentional, not a collision.
// `onConstruct` is the same shape as beginPlay for this purpose (its own dispatch is
// EventRuntime.fireEvent('onConstruct'), which walks collectGroupStarters exactly like
// 'beginPlay' does), so it is likewise NOT in this set.
const GLOBAL_UNIQUE_TYPES = new Set(['voxyConfig', 'convorcherSession', 'headTrackedCamera'])

// Which GLOBAL_UNIQUE_TYPES already exist somewhere OTHER than the pool about to be added into:
// the top-level graph, and every graphGroup's inner nodes except `excludeGroupId` (the group
// being edited, whose own nodes the caller checks locally instead).
function globalUniqueTypesInUse(topNodes, excludeGroupId = null) {
  const found = new Set()
  for (const n of topNodes) {
    if (GLOBAL_UNIQUE_TYPES.has(n.type)) found.add(n.type)
    if (n.type === 'graphGroup' && n.id !== excludeGroupId) {
      for (const inner of n.data?.innerNodes ?? []) {
        if (GLOBAL_UNIQUE_TYPES.has(inner.type)) found.add(inner.type)
      }
    }
  }
  return found
}

const TYPE_COLOR    = { bool: '#d06060', float: '#60a0d0', int: '#d0a060', string: '#c060e8', array: '#30c0a0', map: '#d09020' }
const TYPE_DEFAULTS = { bool: false, float: 0, int: 0, string: '', array: [], map: {} }

// Persist across HMR reloads — module-level vars reset but window does not
if (!window.__evNodeId__) window.__evNodeId__ = 1
const nextEvId = () => `ev_${window.__evNodeId__++}`

const nextVarId = () => `var_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`

const INITIAL_EVENT_NODES = [
  { id: 'onConstruct_default',      type: 'onConstruct',         position: { x: 80,  y: 80  }, data: {}, deletable: false },
  { id: 'onPrefabTrigger_default',  type: 'onPrefabTrigger',     position: { x: 80,  y: 240 }, data: {} },
  { id: 'playFirst_default',        type: 'playFirstSentence',   position: { x: 380, y: 240 }, data: {} },
  { id: 'onSentCompleted_default',  type: 'onSentenceCompleted', position: { x: 80,  y: 400 }, data: {} },
  { id: 'playNext_default',         type: 'playNextSentence',    position: { x: 380, y: 400 }, data: {} },
]

const INITIAL_EVENT_EDGES = [
  { id: 'e_newResp_playFirst', source: 'onPrefabTrigger_default', sourceHandle: 'execOut', target: 'playFirst_default',       targetHandle: 'execIn', type: 'default' },
  { id: 'e_sentComp_playNext', source: 'onSentCompleted_default', sourceHandle: 'execOut', target: 'playNext_default',        targetHandle: 'execIn', type: 'default' },
]

// ─── Context menu ─────────────────────────────────────────────────────────────
function ContextMenu({ screenX, screenY, existingTypes, onAdd, onFocusExisting, onPickExistingNode, onClose, isFunctionEditor = false, isGroupEditor = false, isMacroEditor = false, functionDefs = [], onAddFunctionCall }) {
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState({})
  const [tooltip, setTooltip] = useState(null)
  const inputRef = useRef(null)
  useEffect(() => { inputRef.current?.focus() }, [])

  const filtered = EVENT_PALETTE.filter(item => {
    // fnOnly items only appear inside the function editor
    if (item.fnOnly && !isFunctionEditor) return false
    // Event nodes don't make sense inside functions
    if (isFunctionEditor && item.category === 'Events') return false
    // One level of nesting only: a group's own palette must not offer 'graphGroup' itself,
    // nor the camera-rig template entries (they mint a graphGroup — see cameraRigTemplates.js
    // instantiateRigAsGroup) — both would produce a nested group, which the runtime
    // (graphGroup.js) deliberately never recurses into.
    if (isGroupEditor && (item.type === 'graphGroup' || item.rig)) return false
    // A macro opens from the MAIN graph only: the drill-in is one level, so a
    // macro inside a group or function body could never be opened.
    if ((isGroupEditor || isFunctionEditor) && item.type === 'macro') return false
    // A macro body is a piece of a chain: no events, no containers, no
    // blueprint-wide singletons (macroGraph.js's isForbiddenInMacro).
    if (isMacroEditor && (item.rig || item.category === 'Events' || isForbiddenInMacro(item.type))) return false
    // Space-insensitive match (Unreal-style): "getc" / "getcharloc" finds
    // "Get Character Location". Also match the node type ("getCharacterLocation").
    const q = search.toLowerCase().replace(/\s+/g, '')
    if (!q) return true
    const hay = (item.label + ' ' + item.desc + ' ' + item.type).toLowerCase().replace(/\s+/g, '')
    return hay.includes(q)
  }).map(item =>
    // Unique node types that already exist stay LISTED, greyed, and click
    // focuses the existing node instead of adding a duplicate. Hiding them
    // entirely (the old behavior) made a node buried under another one read
    // as "this node type doesn't exist" — three people debugged the mic that
    // way before anyone found the covered Voxy Config node (2026-07-16).
    item.unique && existingTypes.has(item.type) ? { ...item, _exists: true } : item
  )

  // Inject defined functions as "Call <name>" entries under Functions category
  const fnItems = functionDefs
    .filter(fn => fn.name.toLowerCase().includes(search.toLowerCase()) || search === '')
    .map(fn => ({ type: 'functionCall', label: `Call ${fn.name}`, desc: `Call the "${fn.name}" function`, color: '#9060d0', category: 'Functions', _functionId: fn.id }))

  const allItems = [...filtered, ...fnItems]

  // Live graph snapshot for the "IN THIS GRAPH" section. Read per render, not
  // memoised: these are module singletons mutated in place, so there is nothing
  // to memo against that would not go stale.
  const graphs = readGraphs()
  const graphMatches = search.trim()
    ? findGraphNodes(graphs, search, { preferGraph: 'event', limit: 32 }).filter(m => m.graph === 'event')
    : []

  // Group by category
  const categories = []
  const byCategory = {}
  for (const item of allItems) {
    const cat = item.category ?? 'Other'
    if (!byCategory[cat]) { byCategory[cat] = []; categories.push(cat) }
    byCategory[cat].push(item)
  }

  // What Enter acts on: exactly the row the user can see at the top. An existing
  // node beats a catalog entry, matching the rendered order.
  const firstShown = graphMatches.length
    ? { _match: graphMatches[0] }
    : (categories.length ? byCategory[categories[0]][0] : null)

  const menuWidth = 210

  return (
    <AnchoredOverlay
      x={screenX} y={screenY} margin={8}
      style={{
        width: menuWidth,
        maxHeight: 420,
        background: 'var(--m-panel)', border: '1px solid var(--m-line-hi)',
        borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
        zIndex: 1000, fontFamily: 'var(--m-font)',
        display: 'flex', flexDirection: 'column',
      }}
      onMouseDown={e => e.stopPropagation()}
      // Stop clicks inside the menu (search input, category headers) from
      // bubbling to the editor wrapper's onClick, which closes the menu. It
      // should only close on a node selection (onClose) or a click outside it.
      onClick={e => e.stopPropagation()}
    >
      <div style={{ padding: '8px 8px 6px', borderBottom: '1px solid var(--m-line)', flexShrink: 0 }}>
        <input
          ref={inputRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') onClose()
            // The rendered order is `allItems` regrouped by category, so acting
            // on filtered[0] fired an entry other than the one shown first.
            if (e.key === 'Enter' && firstShown) {
              if (firstShown._match) onPickExistingNode?.(firstShown._match)
              else if (firstShown._exists) onFocusExisting?.(firstShown.type)
              else onAdd(firstShown.type, firstShown._functionId)
              onClose()
            }
          }}
          placeholder="Search nodes..."
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
            color: 'var(--m-fg-2)', padding: '4px 8px', borderRadius: 4,
            fontSize: 11, outline: 'none', fontFamily: 'var(--m-font)',
          }}
        />
      </div>

      <div style={{ overflowY: 'auto', padding: '4px 0' }}>
        {/* What is already HERE comes first: the report is that search only ever
            offered to add another one. Adding is still below, unchanged. */}
        <PaletteGraphMatches
          query={search}
          graphs={graphs}
          graph="event"
          onPick={(m) => { onPickExistingNode?.(m); onClose() }}
        />
        {categories.length === 0 && graphMatches.length === 0 && (
          <div style={{ color: 'var(--m-fg-3)', fontSize: 11, padding: '10px 12px' }}>No results</div>
        )}
        {categories.map(cat => (
          <div key={cat}>
            <div
              onClick={() => setCollapsed(c => ({ ...c, [cat]: !c[cat] }))}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 10px', cursor: 'pointer',
                color: 'var(--m-fg-3)', fontSize: 10, letterSpacing: 0.8,
                userSelect: 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--m-fg-2)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--m-fg-3)'}
            >
              <span style={{ fontSize: 8 }}>{collapsed[cat] ? '▶' : '▼'}</span>
              {cat.toUpperCase()}
            </div>

            {!collapsed[cat] && byCategory[cat].map(item => (
              <div
                key={item._functionId ?? item.type}
                data-testid={`node-menu-item-${item.type}`}
                data-exists={item._exists ? 'true' : undefined}
                onClick={() => {
                  if (item._exists) onFocusExisting?.(item.type)
                  else onAdd(item.type, item._functionId)
                  onClose()
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--m-panel-hi)'
                  const r = e.currentTarget.getBoundingClientRect()
                  setTooltip({ item, x: r.right + 6, y: r.top })
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent'
                  setTooltip(null)
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                  padding: '6px 10px 6px 22px', cursor: 'pointer',
                  borderLeft: `3px solid ${item.color}`,
                  marginLeft: 8, marginRight: 6,
                  borderRadius: '0 3px 3px 0',
                  background: 'transparent',
                  opacity: item._exists ? 0.5 : 1,
                }}
              >
                <span style={{ color: item.color, fontSize: 11 }}>{item.label}</span>
                {item._exists && (
                  <span style={{ color: 'var(--m-fg-3)', fontSize: 9, flexShrink: 0 }}>in graph ↗</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {tooltip && (
        <AnchoredOverlay portal x={tooltip.x} y={tooltip.y} style={{
          background: 'var(--m-panel)',
          border: `1px solid ${tooltip.item.color}44`,
          borderRadius: 4,
          padding: '6px 10px',
          zIndex: 1001,
          maxWidth: 190,
          pointerEvents: 'none',
        }}>
          <div style={{ color: tooltip.item.color, fontSize: 10, fontWeight: 'bold', marginBottom: 3, fontFamily: 'var(--m-font)' }}>
            {tooltip.item.label}
          </div>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 10, fontFamily: 'var(--m-font)', lineHeight: 1.4 }}>
            {tooltip.item.desc}
          </div>
          {tooltip.item._exists && (
            <div style={{ color: 'var(--m-fg-2)', fontSize: 10, fontFamily: 'var(--m-font)', lineHeight: 1.4, marginTop: 4 }}>
              Already in graph (only one allowed) — click to jump to it.
            </div>
          )}
        </AnchoredOverlay>
      )}
    </AnchoredOverlay>
  )
}


// ─── EventCanvas — uses useReactFlow, must live inside ReactFlowProvider ──────
// ─── Event-graph clipboard host ─────────────────────────────────────────────
//
// The event graph has THREE canvases — the main graph, a graphGroup drill-in and
// a function body — and each owns its own nodes/edges state and its own undo
// history. This builds the clipboard host for one of them, so the three cannot
// disagree about what a paste does; only the state handles differ.
//
// Every paste is scoped to the canvas it happened on. A group's inner graph is
// its own namespace, and all three mint from the same ev_ counter
// (window.__evNodeId__), so a node pasted anywhere is unique everywhere.
// Macro tunnels belong to one macro body and mean nothing anywhere else; a
// macro itself can only be opened from the main graph (one drill-in level).
const isNotPastableInSubgraph = t => t === 'macro' || isMacroTunnelType(t)

function useEventClipboardHost({ setNodes, setEdges, history, nodesRef, edgesRef, variables = [], functions = {}, rejectType = null }) {
  const applyPaste = useCallback((pastedNodes, pastedEdges) => {
    // A canvas refuses what it cannot hold (a macro body: events, containers;
    // anywhere: another body's tunnels) — dropped with their wires and
    // reported, never pasted silently.
    if (rejectType) {
      const dropped = pastedNodes.filter(n => rejectType(n.type))
      if (dropped.length) {
        const gone = new Set(dropped.map(n => n.id))
        pastedNodes = pastedNodes.filter(n => !gone.has(n.id))
        pastedEdges = (pastedEdges ?? []).filter(e => !gone.has(e.source) && !gone.has(e.target))
        console.warn(`[EventGraph] cannot paste ${[...new Set(dropped.map(n => n.type))].join(', ')} here — left out of the paste`)
        if (pastedNodes.length === 0) return
      }
    }
    history.push(nodesRef.current, edgesRef.current)
    // Paste selects what it pasted, so an immediate drag or second paste acts
    // on the new copy rather than the original (Unreal's behaviour).
    setNodes(ns => [
      ...(ns ?? []).map(n => (n.selected ? { ...n, selected: false } : n)),
      ...pastedNodes.map(n => ({ ...n, selected: true })),
    ])
    if (pastedEdges?.length) setEdges(es => [...(es ?? []), ...pastedEdges])
  }, [history, nodesRef, edgesRef, setNodes, setEdges, rejectType])

  const removeNodes = useCallback((ids) => {
    // Cut obeys `deletable: false` as Delete does: a macro's tunnels (and a
    // function's entry) would otherwise go, and paste refuses to bring them back.
    const locked = new Set((nodesRef.current ?? []).filter(n => n.deletable === false).map(n => n.id))
    const kill = new Set(ids.filter(id => !locked.has(id)))
    history.push(nodesRef.current, edgesRef.current)
    setNodes(ns => (ns ?? []).filter(n => !kill.has(n.id)))
    setEdges(es => (es ?? []).filter(e => !kill.has(e.source) && !kill.has(e.target)))
  }, [history, nodesRef, edgesRef, setNodes, setEdges])

  return useMemo(() => ({
    kind: 'events',
    source: { character: resolveCharacterId?.() ?? null, graph: 'events' },
    getDeclarations: () => ({ variables, functions }),
    getAvailable: () => availableRefsFor('events', { variables, functions }),
    // Seeded from the LIVE graph per paste, with the shared window counter as a
    // FLOOR so ids stay unique against both mechanisms at once.
    // Seeded from the LIVE graph per paste, with the shared window counter as a
    // FLOOR so ids stay unique against both mechanisms — and the counter is
    // pushed PAST what we mint, or the next nextEvId() from the palette would
    // hand back an id a paste just used.
    makeMinter: () => {
      const mint = createMinter(nodesRef.current, 'ev', { floor: window.__evNodeId__ ?? 1 })
      return () => {
        const id = mint()
        const n = Number.parseInt(id.slice(3), 10)
        if (Number.isFinite(n)) window.__evNodeId__ = Math.max(window.__evNodeId__ ?? 1, n + 1)
        return id
      }
    },
    applyPaste,
    removeNodes,
  }), [variables, functions, applyPaste, removeNodes])
}

function EventCanvas({ nodesWithHandlers, edges, onNodesChange, onEdgesChange, onConnect, onBeforeDelete, addNodeAt, focusRef, onDeleteNode, onDuplicateNode, onArrange, clipboardHost = null, isFunctionEditor = false, isGroupEditor = false, isMacroEditor = false, onCollapseToMacro = null, functionDefs = [], extraExistingTypes, focusContainerId = null }) {
  const { screenToFlowPosition, setCenter, getNode, getNodes, getEdges, fitView } = useReactFlow()
  // Serves a Ctrl+F / agent focus request scoped to exactly this canvas — the
  // top-level graph (null) or the inside of one group / function.
  // Keep this canvas's framing across a close (see useGraphViewport). Scoped to
  // the container, because the function and group drill-ins are their own
  // ReactFlow instances with their own coordinate space.
  const viewportProps = useRememberedViewport({ surface: 'events', containerId: focusContainerId })

  useCanvasFocus({
    graph: 'event',
    containerId: focusContainerId,
    onSelectNode: useCallback(
      (id) => onNodesChange(selectOnlyChanges(nodesWithHandlers, id)),
      [onNodesChange, nodesWithHandlers],
    ),
  })
  const [contextMenu, setContextMenu] = useState(null)
  const [nodeMenu,    setNodeMenu]    = useState(null)

  // ── Clipboard (copy / cut / paste) ────────────────────────────────────────
  // Assembled here because only this component sees the live selection (the
  // parent's controlled `nodes` carry no `selected` flag). Mutations land in
  // the parent's applyPaste / removeNodes so they ride its setNodes → module
  // graph-ref sync, autosave and undo history.
  //
  // ONE hook serves all three event canvases — the main graph, a graphGroup
  // drill-in and a function body — because each mounts its own EventCanvas with
  // its own host. That is also why paste must be scoped: a group's inner graph
  // is its own namespace, and its host mints ids from the same ev_ counter.
  const flowApiRef = useRef(null)
  flowApiRef.current = { screenToFlowPosition }
  const clipboard = useGraphClipboard(
    useMemo(() => ({
      ...(clipboardHost ?? {}),
      getGraph: () => ({ nodes: getNodes(), edges: getEdges() }),
    }), [clipboardHost, getNodes, getEdges]),
    // Each event canvas is its own surface: the main graph, a group drill-in and
    // a function body are all mounted at once, so an unscoped message would
    // caption every one of them.
    { surface: focusContainerId ? `events:${focusContainerId}` : 'events', enabled: !!clipboardHost, flowRef: flowApiRef },
  )

  // Expose focus function to the left panel
  focusRef.current = useCallback((nodeId) => {
    const node = getNode(nodeId)
    if (!node) return
    setCenter(
      node.position.x + (node.measured?.width  ?? 200) / 2,
      node.position.y + (node.measured?.height ?? 80)  / 2,
      { zoom: 1.1, duration: 350 },
    )
  }, [getNode, setCenter])

  // ── Arrange (ENG-1069) ────────────────────────────────────────────────────
  // Same split as the anim editor: compute from the measured/selected nodes
  // here, apply through the parent's setNodes (auto-save + undo). Shared by the
  // top-level / function / group canvases via their own onArrange handlers.
  const runArrange = useCallback((op) => {
    if (!onArrange) return
    const geo = getNodes().map(n => ({
      id: n.id,
      position: n.position,
      width: n.measured?.width ?? n.width,
      height: n.measured?.height ?? n.height,
      selected: n.selected,
    }))
    const selIds = geo.filter(n => n.selected).map(n => n.id)
    let patches = []
    if (op === 'tidy') patches = tidyGraph(geo, edges, {})
    else if (op === 'straighten') patches = straightenConnections(geo, edges, selIds)
    else if (op.startsWith('align:')) patches = alignNodes(geo, selIds, op.slice(6))
    else if (op.startsWith('distribute:')) patches = distributeNodes(geo, selIds, op.slice(11))
    if (patches.length === 0) return
    onArrange(patches)
    if (op === 'tidy') setTimeout(() => fitView({ padding: 0.4, duration: 300 }), 40)
  }, [onArrange, getNodes, edges, fitView])

  const hoveredRef = useRef(false)
  useEffect(() => {
    const onKey = (e) => {
      if (!hoveredRef.current) return
      const t = e.target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      const op = arrangeShortcutFor(e)
      if (!op) return
      e.preventDefault()
      runArrange(op)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [runArrange])

  // extraExistingTypes: GLOBAL_UNIQUE_TYPES (voxyConfig/convorcherSession) already present in a
  // DIFFERENT pool (top-level vs. a group) — greyed here too so the palette doesn't offer a
  // duplicate that addNodeAt would silently refuse anyway. See globalUniqueTypesInUse.
  const existingTypes = new Set([...nodesWithHandlers.map(n => n.type), ...(extraExistingTypes ?? [])])

  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault()
    setNodeMenu(null)
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY })
    setContextMenu({ screenX: e.clientX, screenY: e.clientY, flowPos })
  }, [screenToFlowPosition])

  // Toolbar "Search" opens the same add-node palette as a right-click.
  const openPalette = useCallback((e) => {
    e.stopPropagation() // else the wrapper's onClick closes the menu we just opened
    setNodeMenu(null)
    const x = e.clientX, y = e.clientY
    setContextMenu({ screenX: x, screenY: y, flowPos: screenToFlowPosition({ x, y }) })
  }, [screenToFlowPosition])

  const closeMenu = useCallback(() => setContextMenu(null), [])
  const closeNodeMenu = useCallback(() => setNodeMenu(null), [])

  // Comments (ENG-1119). The event graph had the palette entry but neither the
  // C chord nor the node-menu row, so the feature looked body/face-only
  // (Andres, in the event graph).
  //
  // Goes through `addNodeAt`, the creation seam this canvas is ALREADY given.
  // The first attempt called setNodes/history directly and crashed the whole
  // event graph — those live in the parent, and this component is shared by the
  // main, group and function editors, each with its own node state. The seam
  // exists precisely so the canvas never needs to know which one it is in.
  const createCommentFromSelection = useCallback((fallbackNodeId) => {
    const all = getNodes()
    let subjects = all.filter(n => n.selected && n.type !== COMMENT_TYPE)
    // Right-click does not select, so the menu row would otherwise drop a
    // default box beside the node it was opened over.
    if (subjects.length === 0 && fallbackNodeId) {
      subjects = all.filter(n => n.id === fallbackNodeId && n.type !== COMMENT_TYPE)
    }
    const bounds = boundsAroundNodes(subjects)
    const rect = bounds ?? {
      ...screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 }),
      ...DEFAULT_COMMENT_SIZE,
    }
    addNodeAt(COMMENT_TYPE, { x: rect.x, y: rect.y }, undefined, rect)
  }, [getNodes, screenToFlowPosition, addNodeAt])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'c' && e.key !== 'C') return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const t = e.target
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
      e.preventDefault()
      createCommentFromSelection()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [createCommentFromSelection])

  const onNodeContextMenu = useCallback((e, node) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu(null)
    setNodeMenu({ screenX: e.clientX, screenY: e.clientY, nodeId: node.id, nodeType: node.type, deletable: node.deletable !== false })
  }, [])

  const handleAdd = useCallback((type, functionId) => {
    if (contextMenu) addNodeAt(type, contextMenu.flowPos, functionId)
  }, [contextMenu, addNodeAt])

  // Jump to the existing instance of a unique node type (menu shows those
  // greyed instead of hiding them — see ContextMenu). Through requestFocus, not
  // focusRef: a unique node is allowed to live one level inside a graphGroup
  // (globalUniqueTypesInUse scans for exactly that), and only requestFocus
  // drills in. focusRef could only ever serve a node on THIS canvas.
  const handleFocusExisting = useCallback((type) => {
    const hit = findGraphNodes(readGraphs(), type, { preferGraph: 'event' })
      .find(m => m.graph === 'event' && m.type === type)
    if (hit) requestFocus({ graph: 'event', nodeId: hit.nodeId, container: hit.container, label: hit.label }).catch(() => {})
  }, [])

  // Go to a node the user picked out of the "IN THIS GRAPH" section.
  const handlePickExistingNode = useCallback((m) => {
    requestFocus({ graph: 'event', nodeId: m.nodeId, container: m.container, label: m.label }).catch(() => {})
  }, [])

  return (
    <div
      style={{ width: '100%', height: '100%', position: 'relative' }}
      onClick={() => { closeMenu(); closeNodeMenu() }}
      onMouseEnter={() => { hoveredRef.current = true }}
      onMouseLeave={() => { hoveredRef.current = false }}
      onPointerMove={clipboard.onPointerMove}
      onPaste={clipboard.onPaste}
    >
      <ReactFlow
        nodes={nodesWithHandlers}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onBeforeDelete={onBeforeDelete}
        onPaneContextMenu={onPaneContextMenu}
        onNodeContextMenu={onNodeContextMenu}
        nodeTypes={EVENT_NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        {...viewportProps}
        fitViewOptions={{ padding: 0.4 }}
        deleteKeyCode="Delete"
        className="aos-graph-events"
        style={{ background: 'var(--m-bg-graph)' }}
        {...GRAPH_MULTISELECT_PROPS}
      >
        <Background variant={BackgroundVariant.Dots} color="var(--m-line-hi)" gap={24} size={1} />
        <Controls style={{ background: 'var(--m-panel)', border: '1px solid var(--m-line)' }} />
        <RightDragPan />
        <GraphMiniMap
          style={{ background: 'var(--m-panel)', border: '1px solid var(--m-line-hi)' }}
          nodeColor={n => {
            if (n.type === 'beginPlay' || n.type === 'onConstruct')       return '#8b1a1a'
            if (n.type === 'printString')                                 return '#1a3060'
            if (n.type === 'playFirstSentence' || n.type === 'playNextSentence' || n.type === 'sendTextInput' || n.type === 'sendTextOut' || n.type === 'sendVoiceTextMessage' || n.type === 'delay' || n.type === 'parlaySTT' || n.type === 'voxyConfig' || n.type === 'convorcherSession') return '#0a4028'
            if (n.type === 'inputKey') return '#7a3010'
            if (n.type === 'graphGroup')                                  return '#123840'
            if (EVENT_TYPES.has(n.type))                                  return '#7a3010'
            return '#334'
          }}
        />
      </ReactFlow>

      {contextMenu && (
        <ContextMenu
          screenX={contextMenu.screenX}
          screenY={contextMenu.screenY}
          existingTypes={existingTypes}
          onAdd={handleAdd}
          onFocusExisting={handleFocusExisting}
          onPickExistingNode={handlePickExistingNode}
          onClose={closeMenu}
          isFunctionEditor={isFunctionEditor}
          isGroupEditor={isGroupEditor}
          isMacroEditor={isMacroEditor}
          functionDefs={functionDefs}
        />
      )}
      {nodeMenu && (
        <NodeContextMenu
          screenX={nodeMenu.screenX}
          screenY={nodeMenu.screenY}
          nodeId={nodeMenu.nodeId}
          nodeType={nodeMenu.nodeType}
          deletable={nodeMenu.deletable}
          onDelete={onDeleteNode}
          onDuplicate={onDuplicateNode}
          onArrange={runArrange}
          onCopy={clipboardHost ? clipboard.doCopy : undefined}
          onCut={clipboardHost ? clipboard.doCut : undefined}
          onComment={createCommentFromSelection}
          onCollapseToMacro={onCollapseToMacro ? (fallbackId) => {
            // The selection, else the node the menu was opened over (right-click
            // does not select) — same rule as Create Comment.
            const sel = getNodes().filter(n => n.selected).map(n => n.id)
            onCollapseToMacro(sel.length ? sel : [fallbackId])
          } : undefined}
          // Through the node's OWN injected onChange — the parent puts one on
          // every node (nodesWithHandlers). updateNodeData lives in the parent
          // and is not in scope here; reaching for it crashed the event graph.
          onSetColor={(id, hex) => {
            nodesWithHandlers.find(n => n.id === id)?.data?.onChange?.('color', hex)
          }}
          onClose={closeNodeMenu}
        />
      )}

      <GraphClipboardStatus status={clipboard.status} />

      {/* Hint */}
      <GraphToolbar
        hint="right-click to add · Del to remove · Ctrl+C/X/V to copy nodes"
        onSearch={openPalette}
        onTidy={() => runArrange('tidy')}
      />
    </div>
  )
}

const nextFnId = () => `fn_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`

// ─── Default node data ─────────────────────────────────────────────────────────
// Shared by the main graph's addNodeAt AND a graph group's inner addNodeAt (camera-in-blueprint
// Phase 3) — a group's inner graph accepts the same node catalog as the main graph (full
// palette minus 'graphGroup'/rig templates, see ContextMenu's isGroupEditor filter), so both
// call sites need the identical default-data table. Rig-template palette entries and
// 'graphGroup' itself produce a whole node (or cluster), not a single node's data, so their
// callers special-case them BEFORE reaching this function.
function defaultNodeData(type, functionId) {
  let data = {}
  if (type === 'print' || type === 'printString') data = { duration: 5, color: '#ffff00' }
  if (type === 'playFaceClip')  data = { clipName: 'listening_neutral_face' }
  if (type === 'playBodyClip')  data = { clipName: '', playSpeed: 1.0 }
  if (type === 'loadEnvironment') data = { environmentId: '', environmentName: '', format: '' }
  if (type === 'loadGarment') data = { garmentId: '', garmentName: '', hideOutfit: true, cullSkin: true, applyFit: false }
  // Defaults mirror liveTuneStore.DEFAULTS.backdrop, so a hand-added node that
  // nobody edits applies the same backdrop the app already had — an inert node,
  // not a surprise repaint.
  if (type === 'setBackdrop')     data = { skyEnabled: false, skyColor: BACKDROP_SKY, groundEnabled: false, groundColor: BACKDROP_GROUND, groundSize: 20, hideEnvironment: false }
  if (type === 'setVariable') data = { varName: '', varValue: '' }
  if (type === 'smoothDamp') data = { current: 0, target: 1, smoothTime: 0.3, maxSpeed: 0 }
  // Say Line carries its line inline when nothing is wired to `in-text`.
  if (type === 'sendTextInput' || type === 'sendTextOut') data = { text: '' }
  if (type === 'random') data = { randomType: 'float', min: 0, max: 1 }
  if (type === 'select') data = { selectType: 'string', optionCount: 2, index: 0 }
  if (type === 'concatString') data = { count: 2 }
  if (type === 'merge')        data = { count: MERGE_MIN_INPUTS }
  if (type === 'sequence')     data = { count: SEQUENCE_MIN_OUTPUTS }
  if (type === 'makeLiteralString') data = { value: '' }
  if (type === 'makeLiteralFloat')  data = { value: 0.0 }
  if (type === 'makeLiteralInt')    data = { value: 0 }
  if (type === 'getVariable')       data = { varName: '' }
  if (type === 'customEvent')       data = { name: '' }
  if (type === 'callCustomEvent')   data = { name: '' }
  // TArray
  if (type === 'arrayGet')    data = { varName: '', index: 0 }
  if (type === 'arraySet')    data = { varName: '', index: 0, value: '' }
  if (type === 'arrayAdd')    data = { varName: '', value: '' }
  if (type === 'arrayRemove') data = { varName: '', index: 0 }
  if (type === 'arrayLength') data = { varName: '' }
  if (type === 'arrayClear')  data = { varName: '' }
  // TMap
  if (type === 'mapGet')      data = { varName: '', key: '', keyType: 'string' }
  if (type === 'mapSet')      data = { varName: '', key: '', keyType: 'string', value: '' }
  if (type === 'mapRemove')   data = { varName: '', key: '', keyType: 'string' }
  if (type === 'mapHasKey')   data = { varName: '', key: '', keyType: 'string' }
  if (type === 'mapLength')   data = { varName: '' }
  if (type === 'mapClear')    data = { varName: '' }
  if (type === 'functionCall') data = { functionId: functionId ?? '' }
  // Wayfinding / movement (ENG-592)
  if (type === 'moveToLocation')    data = { x: 0, y: 0, z: 0, acceptanceRadius: 0.2 }
  if (type === 'moveToActor')       data = { target: '' }
  if (type === 'moveToRandomPoint') data = { centerX: 0, centerY: 0, centerZ: 0, radius: 3 }
  // Camera (camera-in-blueprint)
  if (type === 'setCameraFov')        data = { fov: 45, blendMs: 0 }
  if (type === 'setCameraTarget')     data = { mode: 'vector', x: 0, y: 1.5, z: 0, blendMs: 0 }
  if (type === 'setCameraCage')       data = { enabled: true, hx: 2.5, hy: 1.25, hz: 2.5, margin: 0.5 }
  if (type === 'setCameraSeeThrough') data = { enabled: true, radius: 0.6, fade: 0.3, auto: false, autoNear: 1.2, autoBelow: true }
  if (type === 'setCameraFollow')     data = { enabled: true, x: 0, y: 1.6, z: 3, space: 'local', damping: 0.12, targetOffsetY: 0 }
  // Camera rig motion. Sensitivity defaults are tuned for raw canvas pixels in:
  // 0.005 rad/px ≈ a 180° orbit across ~630px of drag; dolly 0.001 ≈ e^0.1 per wheel notch.
  if (type === 'cameraAddYaw')        data = { delta: 0, sensitivity: 0.005, invert: false }
  if (type === 'cameraAddPitch')      data = { delta: 0, sensitivity: 0.005, invert: false }
  if (type === 'cameraAddDolly')      data = { delta: 0, sensitivity: 0.001, invert: false }
  if (type === 'cameraPanTarget')     data = { dx: 0, dy: 0, sensitivity: 0.0015 }
  if (type === 'cameraSetRigLimits')  data = { minDist: 0.6, maxDist: 0, minPitch: -89, maxPitch: 89 }
  if (type === 'cameraSetRigPose')    data = { yaw: 0, pitch: 10, dist: 3, x: 0, y: 1.35, z: 0, blendMs: 0 }
  if (type === 'headTrackedCamera')   data = { ...HEAD_TRACK_DEFAULTS }
  // User input
  if (type === 'onPointerDown')       data = { button: 'left' }
  if (type === 'onPointerUp')         data = { button: 'left' }
  if (type === 'onPointerDrag')       data = { button: 'left' }
  if (type === 'onWheel')             data = {}
  if (type === 'onKeyDown')           data = { key: '' }
  if (type === 'onKeyUp')             data = { key: '' }
  if (type === 'isButtonHeld')        data = { button: 'left' }
  if (type === 'isKeyHeld')           data = { key: '' }
  if (type === 'getInputAxis')        data = { axis: 'mouseX' }
  // Groups (camera-in-blueprint Phase 3) — empty container, no exec/data pins in v1.
  if (type === 'graphGroup')          data = { name: 'Graph Group', innerNodes: [], innerEdges: [] }
  if (type === 'macro')               data = defaultMacroData()
  return data
}

// ─── Main EventGraph component ────────────────────────────────────────────────
export function EventGraph({
  variables, onVariablesChange,
  eventNodes:      initialEventNodes,
  eventEdges:      initialEventEdges,
  onEventNodesChange,
  onEventEdgesChange,
  functions:        initialFunctions = {},
  onFunctionsChange,
  nodeLastFired = {},
  // { read(), canWrite(), write(graph, {nodes, edges}) } over the body/face
  // graphs — see BlueprintEditor. Null in a standalone mount: the rail then
  // treats those graphs as empty (no cross-graph delete, nothing to refuse).
  animGraphs = null,
}) {
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState('bool')
  // ── Function view / Group view ── mutually exclusive drill-in states (camera-in-blueprint
  // Phase 3 generalizes the function drill-in to graph groups — see openFunction/openGroup).
  const [funcView, setFuncView] = useState(null)   // null = main graph, functionId string = function editor
  const [groupView, setGroupView] = useState(null) // null = main graph, top-level node id = group editor
  const openFunction = useCallback((id) => { setGroupView(null); setFuncView(id) }, [])
  const openGroup    = useCallback((id) => { setFuncView(null); setGroupView(id) }, [])
  const [newItemType,  setNewItemType]  = useState('string')
  const [newKeyType,   setNewKeyType]   = useState('string')
  const [newValueType, setNewValueType] = useState('string')
  const [editingVarId, setEditingVarId] = useState(null)

  // ── Canvas state ──
  const [nodes, setNodes, onNodesChange] = useNodesState(initialEventNodes ?? INITIAL_EVENT_NODES)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEventEdges ?? INITIAL_EVENT_EDGES)
  const focusRef = useRef(() => {})

  // Ctrl+F / agent focus: drill into the container holding the target (a graph
  // group or a function), or pop back out to the main graph for a top-level
  // node — otherwise a request can never be served while the user is sitting
  // inside a sub-graph, and it would time out looking like a broken search.
  const currentContainerId = useCallback(() => funcView ?? groupView ?? null, [funcView, groupView])
  const nodesRef = useRef(nodes); nodesRef.current = nodes
  const openFocusContainer = useCallback((containerId) => {
    if (!containerId) { setFuncView(null); setGroupView(null); return }
    // A function id and a group's node id come from different pools; ask the
    // live state which this is rather than guessing, and do nothing for an id
    // that is neither (the request then times out honestly).
    if (initialFunctions?.[containerId]) openFunction(containerId)
    else if (nodesRef.current.some(n => n.id === containerId && (n.type === 'graphGroup' || n.type === 'macro'))) openGroup(containerId)
  }, [initialFunctions, openFunction, openGroup])
  useFocusDrill({ graph: 'event', onOpenContainer: openFocusContainer, currentContainerId })

  // Advance ID counter past all saved ev_* IDs (main graph + function bodies)
  useEffect(() => {
    const advance = (id) => {
      const m = id?.match(/^ev_(\d+)$/)
      if (m) window.__evNodeId__ = Math.max(window.__evNodeId__, parseInt(m[1]) + 1)
    }
    for (const n of nodes) {
      advance(n.id)
      // One level into graphGroup containers (camera-in-blueprint Phase 3) — inner nodes are
      // freshly minted via the same nextEvId() counter (GroupEditor's addNodeAt), so a saved
      // graph whose group holds the highest ev_ id must still advance the counter past it.
      if (n.type === 'graphGroup' || n.type === 'macro') for (const inner of n.data?.innerNodes ?? []) advance(inner.id)
    }
    for (const fn of Object.values(initialFunctions)) {
      for (const n of fn.nodes ?? []) advance(n.id)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync upward + to eventGraphState
  useEffect(() => {
    onEventNodesChange?.(nodes)
    eventGraphState.nodes = nodes
  }, [nodes]) // eslint-disable-line react-hooks/exhaustive-deps

  // Merge a node inserted from outside the editor (CameraRigNotice's live "Add Orbit Rig")
  // into the canvas so it shows immediately; the [nodes] effect above then flows it upward and
  // back into eventGraphState. Append-and-dedup — never touches existing nodes.
  useEffect(() => subscribeEventGraphInsert(node => {
    setNodes(ns => ns.some(n => n.id === node.id) ? ns : [...ns, node])
  }), [setNodes])

  useEffect(() => {
    onEventEdgesChange?.(edges)
    eventGraphState.edges = edges
  }, [edges]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    eventGraphState.functions = initialFunctions
  }, [initialFunctions])

  // Inject onChange into node data
  const updateNodeData = useCallback((nodeId, key, value) => {
    setNodes(ns => ns.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n
    ))
  }, [setNodes])

  // Patch a top-level graphGroup node's inline data (innerNodes/innerEdges/name) — the sync
  // target GroupEditor writes through, mirroring updateFunctionDef for functions.
  const updateGroupNode = useCallback((nodeId, patch) => {
    setNodes(ns => ns.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n
    ))
  }, [setNodes])

  const functionDefs = Object.values(initialFunctions)

  const nodesWithHandlers = useStableNodeData(nodes, n => ({
    onChange:          (key, value) => updateNodeData(n.id, key, value),
    _debugLastFired:   nodeLastFired[n.id] ?? 0,
    variables,
    // Call Custom Event's name suggestions: every custom event in the graph.
    customEventNames:  n.type === 'callCustomEvent' ? customEventNames(eventGraphState.nodes) : undefined,
    functions:         initialFunctions,
    onOpenFunction:    n.type === 'functionCall' ? () => openFunction(n.data.functionId) : undefined,
    onOpenGroup:       (n.type === 'graphGroup' || n.type === 'macro') ? () => openGroup(n.id) : undefined,
  }), [nodeLastFired, variables, initialFunctions, updateNodeData, openFunction, openGroup])

  const onConnect = useCallback((params) => {
    // Unreal capacity rules (ENG-877): displace the old wire instead of
    // keeping both — see graphConnectPolicy.
    setEdges(es => addEdge({ ...params, type: 'default' }, dropDisplacedEdges(es, params)))
  }, [setEdges])

  // ── Undo history (ENG-878) ──
  const history = useRef(createGraphHistory()).current
  const nodesRefU = useRef(nodes); nodesRefU.current = nodes
  const edgesRefU = useRef(edges); edgesRefU.current = edges

  // One snapshot per delete GESTURE (Delete key / edge ×): onBeforeDelete
  // fires once with the full pre-delete state.
  const onBeforeDelete = useCallback(async () => {
    history.push(nodesRefU.current, edgesRefU.current)
    return true
  }, [history])

  // Arrange (ENG-1069): apply the child-computed position patch through
  // setNodes so it rides the same auto-save + undo path as any edit.
  const onArrange = useCallback((patches) => {
    if (!patches || patches.length === 0) return
    history.push(nodesRefU.current, edgesRefU.current)
    const posById = new Map(patches.map(p => [p.id, p.position]))
    setNodes(ns => ns.map(n => posById.has(n.id) ? { ...n, position: posById.get(n.id) } : n))
  }, [history, setNodes])

  // Ctrl/Cmd+Z restores the last deletion. Guarded to the MAIN canvas —
  // the function/group drill-ins own their state and undo — and to
  // non-input targets so text fields keep native undo.
  // A DECLARATION snapshot (variable / function delete from the rail, see
  // graphHistory.js) is not canvas-scoped, so it undoes from a drill-in too.
  const drillRef = useRef(null); drillRef.current = funcView || groupView
  useEffect(() => {
    const onKey = (e) => {
      if (!isUndoKeyEvent(e)) return
      const top = history.peek()
      if (!top) return
      if (drillRef.current && !top.restore) return
      const snap = history.pop()
      e.preventDefault()
      if (snap.restore) { snap.restore(); return }
      setNodes(snap.nodes)
      setEdges(snap.edges)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [history, setNodes, setEdges])

  // ── Add node at flow position (from right-click) ──
  const addNodeAt = useCallback((type, position, functionId, size) => {
    // Enforce uniqueness for nodes marked unique in palette. Event-category unique types are
    // scoped to nodes visible on THIS canvas (top-level here); GLOBAL_UNIQUE_TYPES
    // (voxyConfig/convorcherSession) must ALSO not already exist inside any graphGroup.
    const palEntry = EVENT_PALETTE.find(p => p.type === type)
    if (palEntry?.unique &&
        (nodes.some(n => n.type === type) || globalUniqueTypesInUse(nodes).has(type))) return

    // Camera rig templates ("presets, as graphs") now insert ONE self-contained graphGroup node
    // (camera-in-blueprint Phase 3) rather than a spliced cluster — after which it's just a
    // group you can open and edit. This is the whole replacement for the deleted
    // setCameraPreset enum. instantiateRigAsGroup mints the group its own inner beginPlay and
    // chains the setup nodes (limits/pose) through it, so they still fire on load — see that
    // function's doc comment in cameraRigTemplates.js.
    if (palEntry?.rig) {
      const tpl = CAMERA_RIG_TEMPLATES[palEntry.rig]
      if (!tpl) { console.warn(`[EventGraph] unknown camera rig template "${palEntry.rig}"`); return }
      const groupNode = instantiateRigAsGroup(tpl, position, nextEvId)
      setNodes(ns => [...ns, groupNode])
      devLog(`[EventGraph] inserted "${tpl.label}" as a graph group — ${groupNode.data.innerNodes.length} nodes, ${groupNode.data.innerEdges.length} edges`)
      return
    }

    const data = defaultNodeData(type, functionId)
    // A comment carries a style size and a negative zIndex; the generic
    // shape below drops both, landing a zero-height box behind everything.
    if (type === COMMENT_TYPE) {
      // Outside the updater: StrictMode runs an updater twice and nextEvId()
      // is impure, so inside it marked an id React discarded.
      const c = createComment({ id: nextEvId(), position, width: size?.width, height: size?.height })
      markCommentForEdit(c.id)
      setNodes(ns => withCommentBehind(ns, c))
      return
    }
    setNodes(ns => [...ns, { id: nextEvId(), type, position, data }])
  }, [nodes, setNodes])

  const clipboardHost = useEventClipboardHost({
    setNodes, setEdges, history, nodesRef: nodesRefU, edgesRef: edgesRefU,
    variables, functions: initialFunctions,
    rejectType: isMacroTunnelType,
  })

  const deleteNode = useCallback((id) => {
    // Context-menu delete bypasses ReactFlow's delete pipeline — snapshot
    // here so Ctrl+Z restores it too (ENG-878).
    history.push(nodesRefU.current, edgesRefU.current)
    setNodes(ns => ns.filter(n => n.id !== id))
    setEdges(es => es.filter(e => e.source !== id && e.target !== id))
  }, [setNodes, setEdges, history])

  const duplicateNode = useCallback((id) => {
    setNodes(ns => {
      const node = ns.find(n => n.id === id)
      if (!node) return ns
      const { onChange, variables: _v, ...cleanData } = node.data
      // A macro / group body gets its own inner ids: per-node runtime state is
      // keyed by id, so a copy sharing them would share it (reidBody).
      const data = Array.isArray(cleanData.innerNodes)
        ? { ...cleanData, ...reidBody(cleanData.innerNodes, cleanData.innerEdges ?? [], nextEvId) }
        : cleanData
      return [...ns, {
        ...node,
        id: nextEvId(),
        position: { x: node.position.x + 40, y: node.position.y + 40 },
        data,
        selected: false,
      }]
    })
  }, [setNodes])

  // ── Collapse to Macro (macroGraph.js) ──
  // Replaces the selection with ONE macro whose tunnel pins carry every wire
  // that crossed the selection boundary, so the graph behaves as it did. One
  // undo step restores the uncollapsed nodes.
  const collapseSelectionToMacro = useCallback((ids) => {
    let edgeN = 0
    const edgeStamp = Date.now().toString(36)
    const r = collapseToMacro(nodesRefU.current, edgesRefU.current, ids, {
      mintId: nextEvId,
      mintEdgeId: () => `me_${edgeStamp}_${++edgeN}`,
    })
    if (r.error) {
      dialog.alert({ title: 'Cannot collapse to macro', body: r.error, variant: 'warning' })
      return
    }
    history.push(nodesRefU.current, edgesRefU.current)
    setNodes(r.nodes)
    setEdges(r.edges)
    devLog(`[EventGraph] collapsed ${ids.length} node(s) into macro ${r.macroNode.id}`)
  }, [history, setNodes, setEdges])

  // A macro's signature edit can leave wires on pins that no longer exist (or
  // changed between exec and data) on the OUTER canvas; GroupEditor prunes
  // the inner side and asks for this one.
  const pruneMacroParentEdges = useCallback((macroId, data) => {
    setEdges(es => prunePinEdges(macroId, data, es, []).parentEdges)
  }, [setEdges])

  // ── Functions ──
  const createFunction = () => {
    const id   = nextFnId()
    const name = `NewFunction`
    const entryNode = { id: 'entry', type: 'functionEntry', position: { x: 80, y: 120 }, data: {}, deletable: false }
    const returnNode = { id: `${id}_ret`, type: 'functionReturn', position: { x: 400, y: 120 }, data: {} }
    const fn = { id, name, inputs: [], outputs: [], nodes: [entryNode, returnNode], edges: [] }
    onFunctionsChange?.({ ...initialFunctions, [id]: fn })
    openFunction(id)
  }

  // ── Declaration delete / duplicate (reference-aware, undoable) ──
  // The rule lives in declarationRefs.js; this is the wiring. Every graph a
  // declaration can be referenced from is read as it is RIGHT NOW: the event
  // canvas from its refs (not the render's `nodes`, which a drill-in may be
  // behind), functions from the prop, body/face through `animGraphs`.
  const variablesRef = useRef(variables); variablesRef.current = variables
  const functionsRef = useRef(initialFunctions); functionsRef.current = initialFunctions
  const collectAllGraphs = useCallback(() => {
    const anim = animGraphs?.read?.() ?? { body: { nodes: [], edges: [] }, face: { nodes: [], edges: [] } }
    return {
      body: anim.body, face: anim.face,
      event: { nodes: nodesRefU.current, edges: edgesRefU.current, functions: functionsRef.current },
    }
  }, [animGraphs])

  /**
   * Find every reference, confirm when there are any, remove them, then run
   * `dropDeclaration`. One undo entry restores all of it. Resolves true when
   * the delete happened.
   */
  const deleteDeclaration = useCallback(async ({ kind, key, label, dropDeclaration, restoreDeclaration }) => {
    const before = collectAllGraphs()
    const refs = findDeclarationRefs(before, { kind, key })
    if (refs.length) {
      const lines = describeRefs(refs)
      const ok = await dialog.confirm({
        title: `Delete ${kind} "${label}"?`,
        body: (
          <div>
            <div style={{ marginBottom: 8 }}>
              {refs.length} node{refs.length === 1 ? '' : 's'} reference{refs.length === 1 ? 's' : ''} it. Deleting the {kind} also removes those references:
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 220, overflowY: 'auto', fontFamily: 'var(--m-mono)', fontSize: 11 }}>
              {lines.map((l, i) => <li key={i} style={{ marginBottom: 3 }}>{l}</li>)}
            </ul>
            <div style={{ marginTop: 8, color: 'var(--m-fg-3)', fontSize: 11 }}>Ctrl+Z in the graph editor undoes the whole delete.</div>
          </div>
        ),
        confirmLabel: 'Delete', danger: true, confirmTestId: 'declaration-delete-confirm',
      })
      if (!ok) return false
    }
    const { graphs: after } = removeDeclarationRefs(before, refs)
    const animTouched = ['body', 'face'].filter(g => after[g] !== before[g])
    if (animTouched.length) {
      // Refuse the WHOLE delete before touching anything, or the event graph
      // loses its nodes while the face graph keeps reading the variable.
      const can = animGraphs?.canWrite?.() ?? { ok: false, reason: 'The body and face graphs are not reachable from this editor.' }
      if (!can.ok) { await dialog.alert({ title: 'Cannot delete', body: can.reason, variant: 'error' }); return false }
    }
    // Snapshot — a closure, because this spans more than one canvas.
    const snapNodes = before.event.nodes, snapEdges = before.event.edges
    const snapFunctions = before.event.functions
    const snapAnim = Object.fromEntries(animTouched.map(g => [g, before[g]]))
    history.push(snapNodes, snapEdges, () => {
      setNodes(snapNodes); setEdges(snapEdges)
      onFunctionsChange?.({ ...functionsRef.current, ...snapFunctions })
      for (const g of animTouched) animGraphs.write(g, snapAnim[g])
      restoreDeclaration()
    })
    // Apply.
    for (const g of animTouched) animGraphs.write(g, after[g])
    if (after.event.nodes !== before.event.nodes) setNodes(after.event.nodes)
    if (after.event.edges !== before.event.edges) setEdges(after.event.edges)
    if (after.event.functions !== before.event.functions) onFunctionsChange?.(after.event.functions)
    dropDeclaration(after.event.functions)
    return true
  }, [collectAllGraphs, animGraphs, history, setNodes, setEdges, onFunctionsChange])

  const deleteFunction = useCallback((id) => {
    const fn = functionsRef.current[id]
    if (!fn) return Promise.resolve(false)
    const index = Object.keys(functionsRef.current).indexOf(id)
    return deleteDeclaration({
      kind: 'function', key: id, label: fn.name,
      dropDeclaration: (functionsAfterRefs) => {
        const updated = { ...functionsAfterRefs }
        delete updated[id]
        onFunctionsChange?.(updated)
        if (funcView === id) setFuncView(null)
      },
      restoreDeclaration: () => {
        // Back at its old position, so the rail reads as before.
        const entries = Object.entries(functionsRef.current).filter(([k]) => k !== id)
        entries.splice(Math.min(index, entries.length), 0, [id, fn])
        onFunctionsChange?.(Object.fromEntries(entries))
      },
    })
  }, [deleteDeclaration, onFunctionsChange, funcView])

  const duplicateFunction = useCallback((id) => {
    const fn = functionsRef.current[id]
    if (!fn) return null
    const dup = duplicateFunctionDef(fn, functionsRef.current, nextFnId)
    onFunctionsChange?.({ ...functionsRef.current, [dup.id]: dup })
    return dup
  }, [onFunctionsChange])

  const renameFunction = (id, name) => {
    if (!name.trim()) return
    onFunctionsChange?.({ ...initialFunctions, [id]: { ...initialFunctions[id], name: name.trim() } })
  }

  const updateFunctionDef = (id, patch) => {
    onFunctionsChange?.({ ...initialFunctions, [id]: { ...initialFunctions[id], ...patch } })
  }

  // ── Variables ──
  const addVariable = () => {
    const name = newName.trim()
    if (!name || variables.find(v => v.name === name)) return
    const base = { id: nextVarId(), name, type: newType, defaultValue: TYPE_DEFAULTS[newType], value: TYPE_DEFAULTS[newType] }
    if (newType === 'array') base.itemType = newItemType
    if (newType === 'map')   { base.keyType = newKeyType; base.valueType = newValueType }
    onVariablesChange([...variables, base])
    setNewName('')
  }

  const deleteVariable = useCallback((id) => {
    const list = variablesRef.current
    const index = list.findIndex(v => v.id === id)
    if (index < 0) return Promise.resolve(false)
    const def = list[index]
    return deleteDeclaration({
      kind: 'variable', key: def.name, label: def.name,
      dropDeclaration: () => onVariablesChange(variablesRef.current.filter(v => v.id !== id)),
      restoreDeclaration: () => {
        const cur = variablesRef.current.filter(v => v.id !== id)
        cur.splice(Math.min(index, cur.length), 0, def)
        onVariablesChange(cur)
      },
    })
  }, [deleteDeclaration, onVariablesChange])

  const duplicateVariable = useCallback((id) => {
    const list = variablesRef.current
    const index = list.findIndex(v => v.id === id)
    if (index < 0) return null
    const dup = duplicateVariableDef(list[index], list, nextVarId)
    const next = [...list]
    next.splice(index + 1, 0, dup)
    onVariablesChange(next)
    return dup
  }, [onVariablesChange])

  // Console / test seam: the SAME handlers the rail's menu and Del key run, so
  // a spec exercises production code. A variable may be named by id or name.
  const declApiRef = useRef(null)
  declApiRef.current = { deleteVariable, duplicateVariable, deleteFunction, duplicateFunction, collectAllGraphs }
  useEffect(() => {
    const byVar = (idOrName) => {
      const v = variablesRef.current.find(x => x.id === idOrName || x.name === idOrName)
      return v?.id ?? null
    }
    window.__BLUEPRINT_DECLARATIONS__ = {
      findRefs: (kind, key) => findDeclarationRefs(declApiRef.current.collectAllGraphs(), { kind, key }),
      deleteVariable: (idOrName) => declApiRef.current.deleteVariable(byVar(idOrName)),
      duplicateVariable: (idOrName) => declApiRef.current.duplicateVariable(byVar(idOrName)),
      deleteFunction: (id) => declApiRef.current.deleteFunction(id),
      duplicateFunction: (id) => declApiRef.current.duplicateFunction(id),
    }
    return () => { delete window.__BLUEPRINT_DECLARATIONS__ }
  }, [])

  // Variable value edits are undoable (Ctrl+Z). One undo step per GESTURE:
  // a drag of the value slider, a run of wheel steps or a typed number fires
  // many changes, so changes to the same variable less than
  // VALUE_EDIT_MERGE_MS apart share the step the first of them pushed.
  const lastValueEdit = useRef({ id: null, t: 0 })
  const updateValue = (id, raw) => {
    const now = performance.now()
    const last = lastValueEdit.current
    if (last.id !== id || now - last.t > VALUE_EDIT_MERGE_MS) {
      const before = variables
      history.push(null, null, () => {
        lastValueEdit.current = { id: null, t: 0 }
        onVariablesChange(before)
      })
    }
    lastValueEdit.current = { id, t: now }
    onVariablesChange(variables.map(v => {
      if (v.id !== id) return v
      let value = raw
      if (v.type === 'bool')   value = raw === 'true' || raw === true
      if (v.type === 'float')  value = parseFloat(raw) || 0
      if (v.type === 'int')    value = parseInt(raw)   || 0
      if (v.type === 'string') value = String(raw)
      // array / map: mutated at runtime by nodes — never coerce from the panel
      return { ...v, value }
    }))
  }

  const updateVarName = (id, name) => {
    if (!name.trim()) return
    if (variables.find(v => v.name === name && v.id !== id)) return
    onVariablesChange(variables.map(v => v.id === id ? { ...v, name: name.trim() } : v))
  }

  // ── Derived: MY BLUEPRINT rail contents ──
  // One walk (graphNodeSearch.collectGraphNodes) so the rail, Ctrl+F and the
  // palette can never disagree about what is in the graph. It reaches one level
  // into graphGroups and into function bodies, which the old `nodes.filter` did
  // not -- a node in the Orbit Camera Rig group was simply absent from the rail.
  const outlineGraphs = { event: { nodes, functions: initialFunctions } }
  const eventGroups  = listNodesByType(outlineGraphs, { graph: 'event' })
    .filter(g => EVENT_TYPES.has(g.type))
  // Everything that is not an event starter. Actions were never listed at all,
  // so half a blueprint had no entry in its own outline.
  const actionGroups = listNodesByType(outlineGraphs, { graph: 'event' })
    .filter(g => !EVENT_TYPES.has(g.type))

  const labelForType = useCallback(
    (type) => EVENT_PALETTE.find(p => p.type === type)?.label ?? type,
    [],
  )

  // Navigate via the focus store, NOT the local focusRef: requestFocus ->
  // useFocusDrill opens the containing group or function first, which is what
  // makes a nested node reachable at all. It also fixes a latent dead click --
  // while a group/function view is open the top-level focusRef still holds a
  // closure over an unmounted ReactFlow instance, so clicking a rail row did
  // nothing and said nothing.
  const focusOutlineNode = useCallback((nodeId) => {
    const hit = [...eventGroups, ...actionGroups].flatMap(g => g.nodes).find(n => n.nodeId === nodeId)
    requestFocus({ graph: 'event', nodeId, container: hit?.container ?? null, label: hit?.label })
      .catch(() => {})
  }, [eventGroups, actionGroups])

  // Currently-open group's top-level node, if any (groupView holds its id — see openGroup).
  // Looked up from live `nodes` every render, same pattern FunctionEditor uses via
  // initialFunctions[funcView], so GroupEditor always sees the latest innerNodes/innerEdges.
  // A macro opens through the same drill-in as a group (GroupEditor's macro mode).
  const groupNode = groupView ? nodes.find(n => n.id === groupView && (n.type === 'graphGroup' || n.type === 'macro')) : null

  // The MY BLUEPRINT rail's width — dragged by its handle, kept in shellLayout.
  const [railWidth, setRailWidth] = useState(() => shellLayout.getState().blueprintRailW)
  useEffect(() => shellLayout.subscribe(st => setRailWidth(st.blueprintRailW)), [])

  return (
    <div style={{ display: 'flex', height: '100%', background: 'var(--m-bg)', fontFamily: 'var(--m-font)' }}>

      {/* ─── MY BLUEPRINT left panel ──────────────────────────────── */}
      {/* Width is user-draggable (the handle after it) and remembered in
          shellLayout. The handle draws the panel's right edge. */}
      <div style={{
        width: railWidth,
        background: 'var(--m-panel)',
        display: 'flex',
        flexDirection: 'column',
        fontSize: 11,
        flexShrink: 0,
      }}>
        {/* Panel title */}
        <div style={{
          padding: '8px 12px',
          borderBottom: '1px solid var(--m-line)',
          color: 'var(--m-fg-3)',
          fontSize: 10,
          letterSpacing: 1.5,
          fontWeight: 'bold',
        }}>
          MY BLUEPRINT
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* ── EVENTS section ── */}
          <Section label="EVENTS">
            <BlueprintOutlineSection
              label="EVENTS"
              groups={eventGroups}
              labelForType={labelForType}
              onFocus={focusOutlineNode}
              emptyHint="Right-click canvas to add events"
            />
          </Section>

          {/* ── ACTIONS section ── every non-event node in the graph, including
              the ones inside groups and functions. Without this the outline
              described only the starters and nothing they drove. */}
          <Section label="ACTIONS">
            <BlueprintOutlineSection
              label="ACTIONS"
              groups={actionGroups}
              labelForType={labelForType}
              onFocus={focusOutlineNode}
              emptyHint="Right-click canvas to add nodes"
              glyph="◆"
              accent={ACTIONS_ACCENT}
            />
          </Section>

          {/* ── FUNCTIONS section ── */}
          <Section label="FUNCTIONS" action={{ label: '＋', onClick: createFunction, title: 'New function' }}>
            {functionDefs.length === 0 ? (
              <div style={{ color: 'var(--m-fg-3)', padding: '6px 12px', fontSize: 10, fontStyle: 'italic' }}>
                No functions defined.
              </div>
            ) : functionDefs.sort((a, b) => a.name.localeCompare(b.name)).map(fn => (
              <FunctionSidebarRow
                key={fn.id}
                fn={fn}
                active={funcView === fn.id}
                onOpen={() => openFunction(fn.id)}
                onDuplicate={() => duplicateFunction(fn.id)}
                onDelete={() => deleteFunction(fn.id)}
              />
            ))}
          </Section>

          {/* ── VARIABLES section ── */}
          <Section label="VARIABLES">
            {variables.length === 0 && (
              <div style={{ color: 'var(--m-fg-3)', padding: '6px 12px', fontSize: 10, fontStyle: 'italic' }}>
                No variables defined.
              </div>
            )}
            {variables.map(v => (
              <VariableRow
                key={v.id}
                variable={v}
                onNameChange={name => updateVarName(v.id, name)}
                onValueChange={val  => updateValue(v.id, val)}
                onDuplicate={() => duplicateVariable(v.id)}
                onDelete={() => deleteVariable(v.id)}
                onEdit={(v.type === 'array' || v.type === 'map') ? () => setEditingVarId(v.id) : undefined}
              />
            ))}
          </Section>

          {/* ── CONSTANTS section (read-only) ── */}
          <Section label="CONSTANTS">
            {Object.keys(constants).length === 0 ? (
              <div style={{ color: 'var(--m-fg-3)', padding: '6px 12px', fontSize: 10, fontStyle: 'italic' }}>
                No constants set.
              </div>
            ) : Object.entries(constants).map(([name, value]) => (
              <div key={name} style={{
                display: 'flex', alignItems: 'center',
                padding: '4px 10px', gap: 5,
                borderBottom: '1px solid var(--m-line)',
              }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#c060e8', flexShrink: 0 }} />
                <span style={{ flex: 1, color: 'var(--m-fg-2)', fontSize: 10, fontFamily: 'var(--m-mono)' }}>{name}</span>
                <span style={{ color: 'var(--m-fg-3)', fontSize: 9, fontFamily: 'var(--m-mono)', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {String(value)}
                </span>
              </div>
            ))}
          </Section>
        </div>

        {/* Add variable footer */}
        <div style={{ padding: '8px 10px', borderTop: '1px solid var(--m-line)', display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1 }}>ADD VARIABLE</div>
          <div style={{ display: 'flex', gap: 4 }}>
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addVariable()}
              placeholder="name"
              style={{
                flex: 1, background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
                color: 'var(--m-fg-2)', borderRadius: 3, padding: '3px 5px',
                fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
              }}
            />
            <select
              value={newType}
              onChange={e => setNewType(e.target.value)}
              style={{
                background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
                color: TYPE_COLOR[newType], borderRadius: 3,
                padding: '3px 5px', fontSize: 10, cursor: 'pointer',
              }}
            >
              <option value="bool">bool</option>
              <option value="float">float</option>
              <option value="int">int</option>
              <option value="string">string</option>
              <option value="array">array</option>
              <option value="map">map</option>
            </select>
          </div>
          {newType === 'array' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 9 }}>item type</span>
              <select
                value={newItemType}
                onChange={e => setNewItemType(e.target.value)}
                style={{ background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)', color: TYPE_COLOR[newItemType], borderRadius: 3, padding: '2px 4px', fontSize: 9, cursor: 'pointer' }}
              >
                <option value="bool">bool</option>
                <option value="float">float</option>
                <option value="int">int</option>
                <option value="string">string</option>
              </select>
            </div>
          )}
          {newType === 'map' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 9 }}>key</span>
              <select
                value={newKeyType}
                onChange={e => setNewKeyType(e.target.value)}
                style={{ background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)', color: TYPE_COLOR[newKeyType] ?? '#d0a060', borderRadius: 3, padding: '2px 4px', fontSize: 9, cursor: 'pointer' }}
              >
                <option value="int">int</option>
                <option value="string">string</option>
              </select>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 9 }}>value</span>
              <select
                value={newValueType}
                onChange={e => setNewValueType(e.target.value)}
                style={{ background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)', color: TYPE_COLOR[newValueType], borderRadius: 3, padding: '2px 4px', fontSize: 9, cursor: 'pointer' }}
              >
                <option value="bool">bool</option>
                <option value="float">float</option>
                <option value="int">int</option>
                <option value="string">string</option>
              </select>
            </div>
          )}
          <button
            onClick={addVariable}
            style={{
              background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
              color: '#3060a0', borderRadius: 3, padding: '4px',
              fontSize: 10, fontFamily: 'var(--m-font)', cursor: 'pointer',
            }}
          >
            + Add Variable
          </button>
        </div>
      </div>
      <ResizeHandle axis="x" storeKey="blueprintRailW" />

      {/* ─── Event canvas / Function editor ──────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Top bar: simulate button or function breadcrumb */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '5px 10px',
          background: 'var(--m-panel)',
          borderBottom: '1px solid var(--m-line)',
          flexShrink: 0,
        }}>
          {funcView ? (
            <>
              <button
                onClick={() => setFuncView(null)}
                style={{
                  background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
                  color: '#5080a0', borderRadius: 4,
                  padding: '4px 10px', fontSize: 10,
                  fontFamily: 'var(--m-font)', cursor: 'pointer',
                }}
              >← Main Graph</button>
              <span style={{ color: '#3a2a5a', fontSize: 9 }}>›</span>
              <span style={{ color: '#9060d0', fontSize: 10, fontFamily: 'var(--m-font)' }}>
                {initialFunctions[funcView]?.name ?? funcView}
              </span>
            </>
          ) : groupNode ? (
            <>
              <button
                onClick={() => setGroupView(null)}
                style={{
                  background: '#0a0e14', border: '1px solid #123840',
                  color: '#4fa0a8', borderRadius: 4,
                  padding: '4px 10px', fontSize: 10,
                  fontFamily: 'var(--m-font)', cursor: 'pointer',
                }}
              >← Main Graph</button>
              <span style={{ color: '#123840', fontSize: 9 }}>›</span>
              {/* Named so a test can assert "the breadcrumb says which group
                  we drilled into" without a bare text match: since 5fcd0978
                  every MY BLUEPRINT row also prints its container's name, so
                  the group name appears many times on screen. */}
              <span data-testid="group-breadcrumb" style={{ color: groupNode.type === 'macro' ? MACRO_ACCENT : '#4fc0c8', fontSize: 10, fontFamily: 'var(--m-font)' }}>
                {groupNode.type === 'macro'
                  ? `⧉ ${groupNode.data?.name || 'Macro'}`
                  : `▣ ${groupNode.data?.name ?? 'Graph Group'}`}
              </span>
            </>
          ) : (
            <span style={{ color: 'var(--m-fg-3)', fontSize: 9, fontFamily: 'var(--m-font)' }}>
              right-click canvas to add nodes
            </span>
          )}
        </div>

        {funcView && initialFunctions[funcView] ? (
          <FunctionEditor
            key={funcView}
            fnDef={initialFunctions[funcView]}
            functions={initialFunctions}
            functionDefs={functionDefs}
            nodeLastFired={nodeLastFired}
            variables={variables}
            onFnDefChange={patch => updateFunctionDef(funcView, patch)}
          />
        ) : groupNode ? (
          <GroupEditor
            key={groupNode.id}
            groupNode={groupNode}
            functions={initialFunctions}
            functionDefs={functionDefs}
            nodeLastFired={nodeLastFired}
            onGroupNodeChange={patch => updateGroupNode(groupNode.id, patch)}
            onPruneParentEdges={data => pruneMacroParentEdges(groupNode.id, data)}
            topNodes={nodes}
            variables={variables}
            openFunction={openFunction}
          />
        ) : (
          <div style={{ flex: 1, position: 'relative' }}>
            <ReactFlowProvider>
              <EventCanvas
                nodesWithHandlers={nodesWithHandlers}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onBeforeDelete={onBeforeDelete}
                onArrange={onArrange}
                addNodeAt={addNodeAt}
                focusRef={focusRef}
                onDeleteNode={deleteNode}
                onDuplicateNode={duplicateNode}
                clipboardHost={clipboardHost}
                onCollapseToMacro={collapseSelectionToMacro}
                functionDefs={functionDefs}
                extraExistingTypes={globalUniqueTypesInUse(nodes)}
              />
            </ReactFlowProvider>
            {/* Shown only while this blueprint drives no camera (mirrors the live-mode
                CameraRigNotice). One click inserts the Orbit rig as a graph group via the same
                addNodeAt('rigOrbit') path as the palette, so it's saved with the blueprint. */}
            {cameraRigState(nodes) !== 'ok' && (
              <div style={{
                position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
                zIndex: 6, display: 'flex', alignItems: 'center', gap: 10,
                background: 'rgba(20,26,34,0.94)', border: `1px solid ${CAMERA_ACCENT}`, borderRadius: 6,
                padding: '6px 10px 6px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
              }}>
                <span style={{ color: '#8fb8cc', fontSize: 11 }}>
                  📷 No camera rig — the live camera won't respond.
                </span>
                <button
                  type="button"
                  onClick={() => addNodeAt('rigOrbit', { x: 80, y: 80 })}
                  data-testid="add-orbit-rig-editor"
                  style={{
                    cursor: 'pointer', background: '#123240', border: `1px solid ${CAMERA_ACCENT}`,
                    borderRadius: 4, padding: '4px 12px', fontFamily: 'var(--m-font)', fontSize: 11,
                    fontWeight: 'bold', color: '#8fe0ff',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#1a4658' }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#123240' }}
                >
                  ＋ Add Orbit Camera Rig
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Collection editor modal — rendered at EventGraph level so it has access to variables */}
      {editingVarId && (() => {
        const v = variables.find(x => x.id === editingVarId)
        if (!v) return null
        return (
          <CollectionEditorModal
            variable={v}
            onSave={newValue => {
              onVariablesChange(variables.map(x => x.id === editingVarId ? { ...x, value: newValue } : x))
              setEditingVarId(null)
            }}
            onClose={() => setEditingVarId(null)}
          />
        )
      })()}
    </div>
  )
}

// ─── FunctionEditor ───────────────────────────────────────────────────────────
function FunctionEditor({ fnDef, functions, functionDefs, nodeLastFired, onFnDefChange, variables = [] }) {
  const [fnNodes, setFnNodes, onFnNodesChange] = useNodesState(fnDef.nodes ?? [])
  const [fnEdges, setFnEdges, onFnEdgesChange] = useEdgesState(fnDef.edges ?? [])
  const focusRef = useRef(() => {})

  // Sync inner graph changes back to function def
  useEffect(() => {
    onFnDefChange({ nodes: fnNodes, edges: fnEdges })
  }, [fnNodes, fnEdges]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateNodeData = useCallback((nodeId, key, value) => {
    setFnNodes(ns => ns.map(n => n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n))
  }, [setFnNodes])

  const nodesWithHandlers = useStableNodeData(fnNodes, n => ({
    onChange:        (key, value) => updateNodeData(n.id, key, value),
    _debugLastFired: nodeLastFired[n.id] ?? 0,
    customEventNames: n.type === 'callCustomEvent' ? customEventNames(eventGraphState.nodes) : undefined,
    functions,
    // The variable dropdowns read data.variables; without this every one of
    // them is EMPTY inside a function body (it already was, for the array/map
    // ops). The prop was here and simply never reached the nodes.
    variables,
    fnDef,
  }), [nodeLastFired, functions, variables, fnDef, updateNodeData])

  const onConnect = useCallback((params) => {
    setFnEdges(es => addEdge({ ...params, type: 'default' }, dropDisplacedEdges(es, params)))
  }, [setFnEdges])

  // ── Undo history (ENG-878) — scoped to this drill-in; the editor unmounts
  // when the view closes, so the keydown listener scopes itself. ──
  const history = useRef(createGraphHistory()).current
  const fnNodesRef = useRef(fnNodes); fnNodesRef.current = fnNodes
  const fnEdgesRef = useRef(fnEdges); fnEdgesRef.current = fnEdges
  const onBeforeDelete = useCallback(async () => {
    history.push(fnNodesRef.current, fnEdgesRef.current)
    return true
  }, [history])
  const clipboardHost = useEventClipboardHost({
    setNodes: setFnNodes, setEdges: setFnEdges, history,
    nodesRef: fnNodesRef, edgesRef: fnEdgesRef,
    variables, functions,
    rejectType: isNotPastableInSubgraph,
  })
  const onArrange = useCallback((patches) => {
    if (!patches || patches.length === 0) return
    history.push(fnNodesRef.current, fnEdgesRef.current)
    const posById = new Map(patches.map(p => [p.id, p.position]))
    setFnNodes(ns => ns.map(n => posById.has(n.id) ? { ...n, position: posById.get(n.id) } : n))
  }, [history, setFnNodes])
  useEffect(() => {
    const onKey = (e) => {
      if (!isUndoKeyEvent(e)) return
      // A variable value field's Ctrl+Z belongs to the MAIN graph's history.
      if (e.target?.dataset && 'graphUndo' in e.target.dataset) return
      const snap = history.pop()
      if (!snap) return
      e.preventDefault()
      setFnNodes(snap.nodes)
      setFnEdges(snap.edges)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [history, setFnNodes, setFnEdges])

  const addNodeAt = useCallback((type, position, functionId, size) => {
    let data = {}
    if (type === 'print' || type === 'printString') data = { duration: 5, color: '#ffff00' }
    if (type === 'delay')          data = { duration: 1 }
    if (type === 'branch')         data = {}
    if (type === 'setVariable')    data = { varName: '', varValue: '' }
    if (type === 'smoothDamp')     data = { current: 0, target: 1, smoothTime: 0.3, maxSpeed: 0 }
    if (type === 'random')         data = { randomType: 'float', min: 0, max: 1 }
    if (type === 'select')         data = { selectType: 'string', optionCount: 2, index: 0 }
    if (type === 'concatString')   data = { count: 2 }
    if (type === 'merge')          data = { count: MERGE_MIN_INPUTS }
    if (type === 'sequence')       data = { count: SEQUENCE_MIN_OUTPUTS }
    if (type === 'makeLiteralString') data = { value: '' }
    if (type === 'makeLiteralFloat')  data = { value: 0.0 }
    if (type === 'makeLiteralInt')    data = { value: 0 }
    if (type === 'getVariable')    data = { varName: '' }
    if (type === 'functionCall')   data = { functionId: functionId ?? '' }
    if (type === 'functionReturn') data = {}
    // A comment carries a style size and a negative zIndex; the generic
    // shape below drops both, landing a zero-height box behind everything.
    if (type === COMMENT_TYPE) {
      // Outside the updater: StrictMode runs an updater twice and nextEvId()
      // is impure, so inside it marked an id React discarded.
      const c = createComment({ id: nextEvId(), position, width: size?.width, height: size?.height })
      markCommentForEdit(c.id)
      setFnNodes(ns => withCommentBehind(ns, c))
      return
    }
    setFnNodes(ns => [...ns, { id: nextEvId(), type, position, data }])
  }, [setFnNodes])

  const deleteNode = useCallback((id) => {
    // Don't allow deleting the locked entry node
    if (id === 'entry') return
    history.push(fnNodesRef.current, fnEdgesRef.current)
    setFnNodes(ns => ns.filter(n => n.id !== id))
    setFnEdges(es => es.filter(e => e.source !== id && e.target !== id))
  }, [setFnNodes, setFnEdges, history])

  const duplicateNode = useCallback((id) => {
    if (id === 'entry') return
    setFnNodes(ns => {
      const node = ns.find(n => n.id === id)
      if (!node) return ns
      const { onChange, functions: _f, fnDef: _d, ...cleanData } = node.data
      return [...ns, { ...node, id: nextEvId(), position: { x: node.position.x + 40, y: node.position.y + 40 }, data: cleanData, selected: false }]
    })
  }, [setFnNodes])

  // ── Signature editor helpers ──
  const addInput = () => {
    const name = `input${(fnDef.inputs?.length ?? 0) + 1}`
    onFnDefChange({ inputs: [...(fnDef.inputs ?? []), { name, type: 'string' }] })
  }
  const removeInput = (idx) => {
    onFnDefChange({ inputs: fnDef.inputs.filter((_, i) => i !== idx) })
  }
  const updateInput = (idx, patch) => {
    onFnDefChange({ inputs: fnDef.inputs.map((p, i) => i === idx ? { ...p, ...patch } : p) })
  }
  const addOutput = () => {
    const name = `output${(fnDef.outputs?.length ?? 0) + 1}`
    onFnDefChange({ outputs: [...(fnDef.outputs ?? []), { name, type: 'string' }] })
  }
  const removeOutput = (idx) => {
    onFnDefChange({ outputs: fnDef.outputs.filter((_, i) => i !== idx) })
  }
  const updateOutput = (idx, patch) => {
    onFnDefChange({ outputs: fnDef.outputs.map((p, i) => i === idx ? { ...p, ...patch } : p) })
  }

  const pinTypes = ['string', 'float', 'int', 'bool']
  const PIN_COLOR = { bool: '#d06060', float: '#60a0d0', int: '#d0a060', string: '#c060e8' }
  const inputSty = {
    background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
    color: 'var(--m-fg-2)', borderRadius: 3, padding: '2px 5px',
    fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Signature editor panel */}
      <div style={{
        background: 'var(--m-panel)', borderBottom: '1px solid var(--m-line)',
        padding: '8px 12px', display: 'flex', gap: 24, flexShrink: 0, flexWrap: 'wrap',
      }}>
        {/* Inputs */}
        <div>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1, marginBottom: 4 }}>INPUTS</div>
          {(fnDef.inputs ?? []).map((pin, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: PIN_COLOR[pin.type] ?? '#888', flexShrink: 0 }} />
              <input
                value={pin.name}
                onChange={e => updateInput(i, { name: e.target.value })}
                style={{ ...inputSty, width: 90 }}
              />
              <select
                value={pin.type}
                onChange={e => updateInput(i, { type: e.target.value })}
                style={{ ...inputSty, color: PIN_COLOR[pin.type] ?? '#aaa', cursor: 'pointer' }}
              >
                {pinTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <span
                onClick={() => removeInput(i)}
                style={{ color: '#301820', cursor: 'pointer', fontSize: 11 }}
                onMouseEnter={e => e.currentTarget.style.color = '#c06060'}
                onMouseLeave={e => e.currentTarget.style.color = '#301820'}
              >✕</span>
            </div>
          ))}
          <button onClick={addInput} style={{ background: 'none', border: '1px solid var(--m-line)', color: '#2a4060', borderRadius: 3, padding: '2px 8px', fontSize: 9, fontFamily: 'var(--m-font)', cursor: 'pointer', marginTop: 2 }}>
            + input
          </button>
        </div>

        {/* Outputs */}
        <div>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1, marginBottom: 4 }}>OUTPUTS</div>
          {(fnDef.outputs ?? []).map((pin, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: PIN_COLOR[pin.type] ?? '#888', flexShrink: 0 }} />
              <input
                value={pin.name}
                onChange={e => updateOutput(i, { name: e.target.value })}
                style={{ ...inputSty, width: 90 }}
              />
              <select
                value={pin.type}
                onChange={e => updateOutput(i, { type: e.target.value })}
                style={{ ...inputSty, color: PIN_COLOR[pin.type] ?? '#aaa', cursor: 'pointer' }}
              >
                {pinTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <span
                onClick={() => removeOutput(i)}
                style={{ color: '#301820', cursor: 'pointer', fontSize: 11 }}
                onMouseEnter={e => e.currentTarget.style.color = '#c06060'}
                onMouseLeave={e => e.currentTarget.style.color = '#301820'}
              >✕</span>
            </div>
          ))}
          <button onClick={addOutput} style={{ background: 'none', border: '1px solid var(--m-line)', color: '#2a4060', borderRadius: 3, padding: '2px 8px', fontSize: 9, fontFamily: 'var(--m-font)', cursor: 'pointer', marginTop: 2 }}>
            + output
          </button>
        </div>

        {/* Rename */}
        <div>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1, marginBottom: 4 }}>NAME</div>
          <input
            value={fnDef.name}
            onChange={e => onFnDefChange({ name: e.target.value })}
            style={{ ...inputSty, width: 140 }}
          />
        </div>

        {/* Colour — tints every Call node for this function (functionColor.js) */}
        <div>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1, marginBottom: 4 }}>COLOR</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="color"
              aria-label="Function color"
              data-testid="function-color"
              value={functionColor(fnDef)}
              onChange={e => onFnDefChange({ color: e.target.value })}
              style={{ width: 28, height: 20, padding: 0, border: '1px solid var(--m-line)', borderRadius: 3, background: 'none', cursor: 'pointer' }}
            />
            {fnDef.color && (
              <button
                onClick={() => onFnDefChange({ color: undefined })}
                title="Back to the default colour"
                style={{ background: 'none', border: '1px solid var(--m-line)', color: 'var(--m-fg-3)', borderRadius: 3, padding: '2px 8px', fontSize: 9, fontFamily: 'var(--m-font)', cursor: 'pointer' }}
              >default</button>
            )}
          </div>
        </div>
      </div>

      {/* Inner graph canvas */}
      <div style={{ flex: 1, position: 'relative' }}>
        <ReactFlowProvider>
          <EventCanvas
            nodesWithHandlers={nodesWithHandlers}
            edges={fnEdges}
            onNodesChange={onFnNodesChange}
            onEdgesChange={onFnEdgesChange}
            onConnect={onConnect}
            onBeforeDelete={onBeforeDelete}
            onArrange={onArrange}
            addNodeAt={addNodeAt}
            focusRef={focusRef}
            onDeleteNode={deleteNode}
            onDuplicateNode={duplicateNode}
            clipboardHost={clipboardHost}
            isFunctionEditor={true}
            functionDefs={functionDefs}
            focusContainerId={fnDef.id ?? null}
          />
        </ReactFlowProvider>
      </div>
    </div>
  )
}

// ─── GroupEditor ────────────────────────────────────────────────────────────
// Drill-in editor for a `graphGroup` node's inner graph (camera-in-blueprint Phase 3).
// GENERALIZES FunctionEditor's canvas-swap machinery — same useNodesState/useEdgesState +
// EventCanvas wiring, same sync-on-change pattern — rather than being a parallel editor. It
// differs from FunctionEditor in exactly the ways a group differs from a function:
//   - no signature panel (a group carries no exec/data pins in v1);
//   - storage is inline on the group's OWN node (data.innerNodes/innerEdges), not a top-level
//     `functions` dict keyed by id, so sync-back goes through `onGroupNodeChange` (a patch
//     applied to that one node) rather than `onFnDefChange`;
//   - the palette is the FULL top-level catalog (events included) minus 'graphGroup' itself and
//     the rig-template entries (both would nest a group — see ContextMenu's isGroupEditor
//     filter) — a function editor additionally bans ALL event nodes, a group does not.
//
// MACRO MODE (groupNode.type === 'macro', macroGraph.js): the same drill-in, plus a
// signature panel for the tunnel pins, a palette that refuses what a macro cannot
// hold, and two locked tunnel nodes (Inputs / Outputs) that cannot be deleted,
// duplicated or pasted.
function GroupEditor({ groupNode, functions, functionDefs, nodeLastFired, onGroupNodeChange, onPruneParentEdges, topNodes = [], variables = [], openFunction }) {
  const isMacro = groupNode.type === 'macro'
  const [grpNodes, setGrpNodes, onGrpNodesChange] = useNodesState(groupNode.data?.innerNodes ?? [])
  const [grpEdges, setGrpEdges, onGrpEdgesChange] = useEdgesState(groupNode.data?.innerEdges ?? [])
  const focusRef = useRef(() => {})
  const macroInputs = groupNode.data?.inputs, macroOutputs = groupNode.data?.outputs
  const macroSig = useMemo(
    () => (isMacro ? { inputs: macroInputs ?? [], outputs: macroOutputs ?? [] } : null),
    [isMacro, macroInputs, macroOutputs],
  )
  const isTunnel = useCallback(id => isMacro && (id === MACRO_IN_ID || id === MACRO_OUT_ID), [isMacro])

  // Signature edit: patch the node, then drop every wire left on a pin that is
  // gone or changed between exec and data — inside here, outside via the parent.
  const onSignatureChange = useCallback((patch) => {
    const next = { ...groupNode.data, ...patch }
    onGroupNodeChange(patch)
    setGrpEdges(es => prunePinEdges(groupNode.id, next, [], es).innerEdges)
    onPruneParentEdges?.(next)
  }, [groupNode.data, groupNode.id, onGroupNodeChange, onPruneParentEdges, setGrpEdges])

  // Sync inner graph changes back to the group node's inline data.
  useEffect(() => {
    onGroupNodeChange({ innerNodes: grpNodes, innerEdges: grpEdges })
  }, [grpNodes, grpEdges]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateNodeData = useCallback((nodeId, key, value) => {
    setGrpNodes(ns => ns.map(n => n.id === nodeId ? { ...n, data: { ...n.data, [key]: value } } : n))
  }, [setGrpNodes])

  const nodesWithHandlers = useStableNodeData(grpNodes, n => ({
    onChange:         (key, value) => updateNodeData(n.id, key, value),
    _debugLastFired:  nodeLastFired[n.id] ?? 0,
    customEventNames: n.type === 'callCustomEvent' ? customEventNames(eventGraphState.nodes) : undefined,
    functions,
    variables,
    onOpenFunction:   n.type === 'functionCall' && openFunction ? () => openFunction(n.data.functionId) : undefined,
    macroSig:         (n.type === 'macroInput' || n.type === 'macroOutput') ? macroSig : undefined,
  }), [nodeLastFired, functions, variables, openFunction, updateNodeData, macroSig])

  const onConnect = useCallback((params) => {
    setGrpEdges(es => addEdge({ ...params, type: 'default' }, dropDisplacedEdges(es, params)))
  }, [setGrpEdges])

  // ── Undo history (ENG-878) — scoped to this drill-in; the editor unmounts
  // when the view closes, so the keydown listener scopes itself. ──
  const history = useRef(createGraphHistory()).current
  const grpNodesRef = useRef(grpNodes); grpNodesRef.current = grpNodes
  const grpEdgesRef = useRef(grpEdges); grpEdgesRef.current = grpEdges
  const onBeforeDelete = useCallback(async () => {
    history.push(grpNodesRef.current, grpEdgesRef.current)
    return true
  }, [history])
  const clipboardHost = useEventClipboardHost({
    setNodes: setGrpNodes, setEdges: setGrpEdges, history,
    nodesRef: grpNodesRef, edgesRef: grpEdgesRef,
    variables, functions,
    rejectType: isMacro ? isForbiddenInMacro : isNotPastableInSubgraph,
  })
  const onArrange = useCallback((patches) => {
    if (!patches || patches.length === 0) return
    history.push(grpNodesRef.current, grpEdgesRef.current)
    const posById = new Map(patches.map(p => [p.id, p.position]))
    setGrpNodes(ns => ns.map(n => posById.has(n.id) ? { ...n, position: posById.get(n.id) } : n))
  }, [history, setGrpNodes])
  useEffect(() => {
    const onKey = (e) => {
      if (!isUndoKeyEvent(e)) return
      // A variable value field's Ctrl+Z belongs to the MAIN graph's history.
      if (e.target?.dataset && 'graphUndo' in e.target.dataset) return
      const snap = history.pop()
      if (!snap) return
      e.preventDefault()
      setGrpNodes(snap.nodes)
      setGrpEdges(snap.edges)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [history, setGrpNodes, setGrpEdges])

  const addNodeAt = useCallback((type, position, functionId, size) => {
    // Event-category uniqueness (e.g. one beginPlay) is scoped to THIS group's own inner graph,
    // never the top-level graph or a sibling group — each group needs its own beginPlay to be
    // reachable at all (EventRuntime dispatches beginPlay against every group's pool
    // independently). GLOBAL_UNIQUE_TYPES (voxyConfig/convorcherSession) are the exception: those
    // must not already exist at the top level or inside any sibling group either, or the runtime
    // double-fires them (double mic init / double session declare).
    if (isMacro && isForbiddenInMacro(type)) return
    const palEntry = EVENT_PALETTE.find(p => p.type === type)
    if (palEntry?.unique) {
      const localDup = grpNodes.some(n => n.type === type)
      const globalDup = GLOBAL_UNIQUE_TYPES.has(type) &&
        globalUniqueTypesInUse(topNodes, groupNode.id).has(type)
      if (localDup || globalDup) return
    }
    // The palette (isGroupEditor filter) already excludes 'graphGroup' and rig templates, so
    // no cluster/nested-group special-casing is needed here — every addable type is a plain node.
    const data = defaultNodeData(type, functionId)
    // A comment carries a style size and a negative zIndex; the generic
    // shape below drops both, landing a zero-height box behind everything.
    if (type === COMMENT_TYPE) {
      // Outside the updater: StrictMode runs an updater twice and nextEvId()
      // is impure, so inside it marked an id React discarded.
      const c = createComment({ id: nextEvId(), position, width: size?.width, height: size?.height })
      markCommentForEdit(c.id)
      setGrpNodes(ns => withCommentBehind(ns, c))
      return
    }
    setGrpNodes(ns => [...ns, { id: nextEvId(), type, position, data }])
  }, [grpNodes, setGrpNodes, topNodes, groupNode.id, isMacro])

  const deleteNode = useCallback((id) => {
    if (isTunnel(id)) return
    history.push(grpNodesRef.current, grpEdgesRef.current)
    setGrpNodes(ns => ns.filter(n => n.id !== id))
    setGrpEdges(es => es.filter(e => e.source !== id && e.target !== id))
  }, [setGrpNodes, setGrpEdges, history, isTunnel])

  const duplicateNode = useCallback((id) => {
    if (isTunnel(id)) return
    setGrpNodes(ns => {
      const node = ns.find(n => n.id === id)
      if (!node) return ns
      const { onChange, functions: _f, macroSig: _m, ...cleanData } = node.data
      return [...ns, { ...node, id: nextEvId(), position: { x: node.position.x + 40, y: node.position.y + 40 }, data: cleanData, selected: false }]
    })
  }, [setGrpNodes, isTunnel])

  return (
    <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {isMacro && <MacroSignaturePanel data={groupNode.data} onChange={onSignatureChange} />}
      <div style={{ flex: 1, position: 'relative' }}>
      <ReactFlowProvider>
        <EventCanvas
          nodesWithHandlers={nodesWithHandlers}
          edges={grpEdges}
          onNodesChange={onGrpNodesChange}
          onEdgesChange={onGrpEdgesChange}
          onConnect={onConnect}
          onBeforeDelete={onBeforeDelete}
          onArrange={onArrange}
          addNodeAt={addNodeAt}
          focusRef={focusRef}
          onDeleteNode={deleteNode}
          onDuplicateNode={duplicateNode}
          clipboardHost={clipboardHost}
          isGroupEditor={!isMacro}
          isMacroEditor={isMacro}
          functionDefs={functionDefs}
          extraExistingTypes={globalUniqueTypesInUse(topNodes, groupNode.id)}
          focusContainerId={groupNode.id}
        />
      </ReactFlowProvider>
      </div>
    </div>
  )
}

// ─── MacroSignaturePanel ─────────────────────────────────────────────────────
// Name + the tunnel pins of a macro. Pins carry a stable id (macroGraph.js), so
// renaming one keeps its wires; removing one, or switching it between exec and
// data, drops the wires it had (GroupEditor's onSignatureChange).
function MacroSignaturePanel({ data, onChange }) {
  const inputs = data.inputs ?? [], outputs = data.outputs ?? []
  const inputSty = {
    background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
    color: 'var(--m-fg-2)', borderRadius: 3, padding: '2px 5px',
    fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
  }
  const btnSty = { background: 'none', border: '1px solid var(--m-line)', color: MACRO_ACCENT, borderRadius: 3, padding: '2px 8px', fontSize: 9, fontFamily: 'var(--m-font)', cursor: 'pointer', marginTop: 2 }
  const column = (key, label, pins, base) => {
    const set = list => onChange({ [key]: list })
    const add = () => {
      // The first pin added to a side with no exec pin is exec; after that, data.
      const exec = pins.every(p => p.type !== 'exec')
      set([...pins, { id: nextPinId(inputs, outputs), name: uniquePinName(exec ? base : 'value', pins), type: exec ? 'exec' : 'float' }])
    }
    return (
      <div data-testid={`macro-${key}`}>
        <div style={{ color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1, marginBottom: 4 }}>{label}</div>
        {pins.map((pin, i) => (
          <div key={pin.id} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
            <div style={{ width: 8, height: 8, borderRadius: pin.type === 'exec' ? 1 : '50%', background: MACRO_PIN_COLOR[pin.type] ?? 'var(--m-fg-3)', flexShrink: 0 }} />
            <input aria-label={`${key} pin name`} value={pin.name}
              onChange={e => set(pins.map((p, j) => j === i ? { ...p, name: e.target.value } : p))}
              style={{ ...inputSty, width: 90 }} />
            <select aria-label={`${key} pin type`} value={pin.type}
              onChange={e => set(pins.map((p, j) => j === i ? { ...p, type: e.target.value } : p))}
              style={{ ...inputSty, color: MACRO_PIN_COLOR[pin.type] ?? 'var(--m-fg-2)', cursor: 'pointer' }}>
              {MACRO_PIN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <span
              role="button" aria-label={`remove ${key} pin ${pin.name}`}
              onClick={() => set(pins.filter((_, j) => j !== i))}
              style={{ color: 'var(--m-fg-3)', cursor: 'pointer', fontSize: 11 }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--m-danger)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--m-fg-3)'}
            >✕</span>
          </div>
        ))}
        <button onClick={add} style={btnSty}>+ {key === 'inputs' ? 'input' : 'output'}</button>
      </div>
    )
  }
  return (
    <div style={{
      background: 'var(--m-panel)', borderBottom: '1px solid var(--m-line)',
      padding: '8px 12px', display: 'flex', gap: 24, flexShrink: 0, flexWrap: 'wrap',
    }}>
      <div>
        <div style={{ color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1, marginBottom: 4 }}>MACRO</div>
        <input aria-label="macro name" data-testid="macro-name" value={data.name ?? ''}
          onChange={e => onChange({ name: e.target.value })}
          style={{ ...inputSty, width: 140 }} />
      </div>
      {column('inputs', 'INPUTS', inputs, 'In')}
      {column('outputs', 'OUTPUTS', outputs, 'Out')}
      <div style={{ color: 'var(--m-fg-3)', fontSize: 9, maxWidth: 260, alignSelf: 'center', lineHeight: 1.4 }}>
        Exec pins route the chain through the macro; Delays and other latent nodes inside
        resume where they left off. Data inputs are read when the body asks for them.
      </div>
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Rail-row context menu (functions and variables). Unreal's convention, and
 * Florent's request (2026-09-11): delete is right-click > Delete or select the
 * row and press Del — not a per-row × button, which reads as clutter. Items are
 * `{ label, onClick, danger?, testId? }`.
 */
function RailRowMenu({ x, y, items, onClose }) {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={onClose} onContextMenu={e => { e.preventDefault(); onClose() }} />
      <AnchoredOverlay
        x={x} y={y}
        style={{
          background: 'var(--m-panel)', border: '1px solid var(--m-line-hi)',
          borderRadius: 5, boxShadow: '0 4px 20px rgba(0,0,0,0.8)',
          zIndex: 1000, fontFamily: 'var(--m-font)', fontSize: 11, overflow: 'hidden',
          minWidth: 130,
        }}
        onMouseDown={e => e.stopPropagation()}
      >
        {items.map((item, i) => (
          <div
            key={item.label}
            role="menuitem"
            data-testid={item.testId}
            onClick={() => { onClose(); item.onClick() }}
            style={{
              padding: '7px 12px', cursor: 'pointer',
              color: item.danger ? '#e06060' : 'var(--m-fg-2)',
              borderBottom: i < items.length - 1 ? '1px solid var(--m-line)' : 'none',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--m-panel-hi)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >{item.label}</div>
        ))}
      </AnchoredOverlay>
    </>
  )
}

/** Del / Backspace on a focused rail row, never from inside its inputs. */
function isRowDeleteKey(e) {
  if (e.key !== 'Delete' && e.key !== 'Backspace') return false
  const t = e.target
  return !(t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable))
}

const ROW_FOCUS_RING = 'inset 2px 0 0 var(--m-accent, #4a80c0)'

function FunctionSidebarRow({ fn, active, onOpen, onDuplicate, onDelete }) {
  const [menu, setMenu] = useState(null)
  const handleContextMenu = (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.focus()
    setMenu({ x: e.clientX, y: e.clientY })
  }
  const onKeyDown = (e) => {
    if (!isRowDeleteKey(e)) return
    e.preventDefault(); e.stopPropagation()
    onDelete()
  }

  return (
    <>
      <div
        tabIndex={0}
        data-testid="rail-function-row"
        data-name={fn.name}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '4px 8px 4px 12px', cursor: 'pointer',
          borderBottom: '1px solid var(--m-line)',
          background: active ? 'var(--m-panel-hi)' : 'transparent',
          outline: 'none',
        }}
        onClick={onOpen}
        onContextMenu={handleContextMenu}
        onKeyDown={onKeyDown}
        onFocus={e => { e.currentTarget.style.boxShadow = ROW_FOCUS_RING }}
        onBlur={e => { e.currentTarget.style.boxShadow = 'none' }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--m-panel-hi)' }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
      >
        <span style={{ color: functionColor(fn), fontSize: 12, fontStyle: 'italic', fontFamily: 'Georgia, serif', lineHeight: 1 }}>ƒ</span>
        <span style={{ color: '#9060d0', fontSize: 10, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {fn.name}
        </span>
      </div>

      {menu && (
        <RailRowMenu
          x={menu.x} y={menu.y} onClose={() => setMenu(null)}
          items={[
            { label: 'Open', onClick: onOpen, testId: 'rail-menu-open' },
            { label: 'Duplicate', onClick: onDuplicate, testId: 'rail-menu-duplicate' },
            { label: 'Delete', onClick: onDelete, danger: true, testId: 'rail-menu-delete' },
          ]}
        />
      )}
    </>
  )
}

function Section({ label, children, action }) {
  const [open, setOpen] = useState(true)
  return (
    <div style={{ borderBottom: '1px solid var(--m-line)' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 10px',
          background: 'var(--m-panel)',
          color: 'var(--m-fg-3)', fontSize: 9, letterSpacing: 1.2, fontWeight: 'bold',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 8, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>{open ? '▼' : '▶'}</span>
        <span style={{ flex: 1, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>{label}</span>
        {action && (
          <span
            title={action.title}
            onClick={e => { e.stopPropagation(); action.onClick() }}
            style={{ color: '#2a4060', fontSize: 13, cursor: 'pointer', lineHeight: 1 }}
            onMouseEnter={e => e.currentTarget.style.color = '#4a80c0'}
            onMouseLeave={e => e.currentTarget.style.color = '#2a4060'}
          >{action.label}</span>
        )}
      </div>
      {open && children}
    </div>
  )
}

function coerceToType(raw, type) {
  if (type === 'bool')  return raw === 'true' || raw === true
  if (type === 'int')   return Math.round(Number(raw)) || 0
  if (type === 'float') return Number(raw) || 0
  return String(raw ?? '')
}

function CollectionEditorModal({ variable, onSave, onClose }) {
  const isArray    = variable.type === 'array'
  const itemType   = variable.itemType  ?? 'string'
  const keyType    = variable.keyType   ?? 'string'
  const valueType  = variable.valueType ?? 'string'
  const accent     = TYPE_COLOR[variable.type] ?? '#888'

  const [items, setItems] = useState(() =>
    isArray
      ? (Array.isArray(variable.value) ? [...variable.value] : [])
      : Object.entries(variable.value ?? {}).map(([k, v]) => ({ k, v: String(v) }))
  )
  const [newKey, setNewKey] = useState('')
  const [newVal, setNewVal] = useState(valueType === 'bool' || itemType === 'bool' ? 'false' : '')

  const effectiveValType = isArray ? itemType : valueType

  const addItem = () => {
    if (isArray) {
      setItems(prev => [...prev, coerceToType(newVal, itemType)])
      setNewVal(itemType === 'bool' ? 'false' : '')
    } else {
      const k = keyType === 'int' ? String(Math.round(Number(newKey)) || 0) : newKey.trim()
      if (!k) return
      setItems(prev => [...prev.filter(i => i.k !== k), { k, v: newVal }])
      setNewKey('')
      setNewVal(valueType === 'bool' ? 'false' : '')
    }
  }

  const removeItem = idx => setItems(prev => prev.filter((_, i) => i !== idx))

  const updateItemVal = (idx, raw) => setItems(prev => prev.map((x, i) => {
    if (i !== idx) return x
    return isArray ? coerceToType(raw, itemType) : { ...x, v: raw }
  }))

  const save = () => {
    if (isArray) {
      onSave(items)
    } else {
      const map = {}
      for (const { k, v } of items) map[k] = coerceToType(v, valueType)
      onSave(map)
    }
  }

  const inputSty = (w) => ({
    width: w, background: 'var(--m-panel-hi)', border: '1px solid var(--m-line)',
    color: 'var(--m-fg-2)', borderRadius: 3, padding: '2px 5px',
    fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
  })
  const btnSty = (color) => ({
    background: 'none', border: `1px solid ${color}50`, color,
    borderRadius: 3, padding: '2px 8px', fontSize: 10,
    fontFamily: 'var(--m-font)', cursor: 'pointer',
  })

  const title = isArray
    ? `${variable.name}  :  array<${itemType}>`
    : `${variable.name}  :  map<${keyType}, ${valueType}>`

  // Called as a plain function, NOT as <ValInput/>, to avoid remount-on-render
  const renderValInput = (value, onChange, width) =>
    effectiveValType === 'bool' ? (
      <select value={String(value)} onChange={e => onChange(e.target.value)}
        style={{ ...inputSty(width ?? 70), cursor: 'pointer' }}>
        <option value="false">false</option>
        <option value="true">true</option>
      </select>
    ) : (
      <input
        type={effectiveValType === 'int' || effectiveValType === 'float' ? 'number' : 'text'}
        step={effectiveValType === 'float' ? 0.1 : 1}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && addItem()}
        style={inputSty(width ?? (isArray ? 200 : 130))}
      />
    )

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{ background: 'var(--m-panel)', border: `1px solid ${accent}40`, borderRadius: 6, padding: 16, minWidth: 340, maxWidth: 480, boxShadow: `0 0 24px ${accent}18`, fontFamily: 'var(--m-font)' }}
        onMouseDown={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ color: accent, fontSize: 11, fontWeight: 'bold', flex: 1 }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--m-fg-3)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>

        {/* Items list */}
        <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {items.length === 0 && <div style={{ color: 'var(--m-fg-3)', fontSize: 10, padding: '4px 0' }}>empty</div>}
          {items.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 9, width: isArray ? 18 : 'auto', textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {isArray ? i : item.k}
              </span>
              <span style={{ color: 'var(--m-fg-3)', fontSize: 10 }}>{isArray ? '→' : ':'}</span>
              {renderValInput(isArray ? String(item) : item.v, raw => updateItemVal(i, raw), isArray ? 220 : 160)}
              <button onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', color: '#4a2020', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>

        {/* Add row */}
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', borderTop: '1px solid var(--m-line)', paddingTop: 10, marginBottom: 14 }}>
          {!isArray && (
            <>
              <input
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addItem()}
                placeholder={keyType === 'int' ? '0' : 'key'}
                type={keyType === 'int' ? 'number' : 'text'}
                style={inputSty(keyType === 'int' ? 55 : 80)}
              />
              <span style={{ color: 'var(--m-fg-3)', fontSize: 10 }}>:</span>
            </>
          )}
          {renderValInput(newVal, setNewVal)}
          <button onClick={addItem} style={btnSty(accent)}>+ Add</button>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <button onClick={onClose} style={btnSty('var(--m-fg-3)')}>Cancel</button>
          <button onClick={save} style={btnSty(accent)}>Save</button>
        </div>
      </div>
    </div>
  )
}

function VariableRow({ variable, onNameChange, onValueChange, onDuplicate, onDelete, onEdit }) {
  const typeColor = TYPE_COLOR[variable.type] ?? '#888'
  const isCollection = variable.type === 'array' || variable.type === 'map'
  const [menu, setMenu] = useState(null)
  const handleContextMenu = (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.focus()
    setMenu({ x: e.clientX, y: e.clientY })
  }
  const onKeyDown = (e) => {
    if (!isRowDeleteKey(e)) return
    e.preventDefault(); e.stopPropagation()
    onDelete()
  }

  // Human-readable type signature e.g. "array<string>" or "map<int,float>"
  const typeLabel = variable.type === 'array'
    ? `array<${variable.itemType ?? '?'}>`
    : variable.type === 'map'
      ? `map<${variable.keyType ?? '?'},${variable.valueType ?? '?'}>`
      : variable.type

  return (
    <>
    <div
      tabIndex={0}
      data-testid="rail-variable-row"
      data-name={variable.name}
      onContextMenu={handleContextMenu}
      onKeyDown={onKeyDown}
      onFocus={e => { if (e.target === e.currentTarget) e.currentTarget.style.boxShadow = ROW_FOCUS_RING }}
      onBlur={e => { e.currentTarget.style.boxShadow = 'none' }}
      // The type lives in the hover text, not as a column: a right-hand type
      // label pushed every value box off-centre (Florent, 2026-10-02).
      title={`${typeLabel}: Right-click for Duplicate / Delete. Del removes the selected variable.`}
      style={{
        display: 'flex', alignItems: 'center',
        padding: '4px 10px', gap: 5, minWidth: 0,
        borderBottom: '1px solid var(--m-line)',
        outline: 'none',
      }}>
      <div style={{ width: 7, height: 7, borderRadius: '50%', background: typeColor, flexShrink: 0 }} />
      <input
        value={variable.name}
        onChange={e => onNameChange(e.target.value)}
        style={{
          flex: 1, minWidth: 0, background: 'transparent', border: 'none',
          borderBottom: '1px solid var(--m-line)', color: 'var(--m-fg-2)',
          fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none', padding: '1px 0',
        }}
      />
      {isCollection ? (
        <>
          <span style={{
            color: `${typeColor}66`, fontSize: 9, fontFamily: 'var(--m-font)',
            background: 'var(--m-panel-hi)', border: `1px solid ${typeColor}20`,
            borderRadius: 2, padding: '1px 5px', whiteSpace: 'nowrap',
          }}>
            {variable.type === 'array'
              ? `[${Array.isArray(variable.value) ? variable.value.length : 0}]`
              : `{${variable.value && typeof variable.value === 'object' ? Object.keys(variable.value).length : 0}}`
            }
          </span>
          <button
            onClick={onEdit}
            title="Edit items"
            style={{
              background: 'none', border: `1px solid ${typeColor}30`, color: typeColor,
              borderRadius: 3, padding: '1px 5px', fontSize: 9,
              fontFamily: 'var(--m-font)', cursor: 'pointer', lineHeight: 1,
            }}
          >
            +
          </button>
        </>
      ) : variable.type === 'bool' ? (
        <select
          value={String(variable.value)}
          onChange={e => onValueChange(e.target.value)}
          data-graph-undo=""
          style={{
            background: 'var(--m-panel-hi)', border: `1px solid ${typeColor}30`,
            color: typeColor, borderRadius: 2, padding: '1px 3px', fontSize: 9, cursor: 'pointer', width: 46,
          }}
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : variable.type === 'string' ? (
        <input
          type="text"
          value={variable.value ?? ''}
          onChange={e => onValueChange(e.target.value)}
          data-graph-undo=""
          style={{
            width: 60, background: 'var(--m-panel-hi)', border: `1px solid ${typeColor}30`,
            color: typeColor, borderRadius: 2, padding: '1px 3px',
            fontSize: 9, fontFamily: 'var(--m-mono)', outline: 'none',
          }}
        />
      ) : (
        <input
          type="number"
          step={variable.type === 'float' ? 0.1 : 1}
          value={variable.value}
          onChange={e => onValueChange(e.target.value)}
          data-graph-undo=""
          style={{
            width: 46, background: 'var(--m-panel-hi)', border: `1px solid ${typeColor}30`,
            color: typeColor, borderRadius: 2, padding: '1px 3px',
            fontSize: 9, fontFamily: 'var(--m-mono)', outline: 'none', textAlign: 'right',
          }}
        />
      )}
    </div>
    {menu && (
      <RailRowMenu
        x={menu.x} y={menu.y} onClose={() => setMenu(null)}
        items={[
          { label: 'Duplicate', onClick: onDuplicate, testId: 'rail-menu-duplicate' },
          { label: 'Delete', onClick: onDelete, danger: true, testId: 'rail-menu-delete' },
        ]}
      />
    )}
    </>
  )
}
